import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Formik, Form, Field } from 'formik';
import { Settings, TrendingUp, RefreshCw, ChevronsRight, Search, AlertTriangle, FileText, X as IconX, Bot, Send, LogIn, LogOut, User, MessageSquare, Calculator, Save, Menu, ArrowLeft, CreditCard, Bolt, ShieldCheck, CheckCircle2, BarChart3, Sparkles } from 'lucide-react';
import Footer from './components/Footer';
import { useNotification } from './hooks/useNotification';
import { formatAIResponseToHTML } from './utils/formatAIResponse';
import { 
  trackCalculation, 
  trackChatMessage, 
  trackChatbotOpen, 
  trackChatbotClose,
  trackAIAnalysis,
  trackTaxFormComparison,
  trackPageView,
  trackButtonClick,
  trackPKDSearch,
  trackModalOpen,
  trackModalClose,
  initScrollTracking,
  initTimeTracking,
  trackError
} from './utils/analytics';

// Lazy loading dla ciężkich komponentów
const LegalPage = lazy(() => import('./components/LegalPage'));
const FAQSection = lazy(() => import('./components/FAQSection'));
const SEOContentSection = lazy(() => import('./components/SEOContentSection'));
const SubscriptionPlans = lazy(() => import('./components/SubscriptionPlans'));
const SubscriptionStatus = lazy(() => import('./components/SubscriptionStatus'));
const LandingPagePodatkowy2026 = lazy(() => import('./components/LandingPagePodatkowy2026'));
const LandingPageZusJDG = lazy(() => import('./components/LandingPageZusJDG'));
const LandingPageB2B2026 = lazy(() => import('./components/LandingPageB2B2026'));
const RevenueChart = lazy(() => import('./components/RevenueChart'));

// --- Komponent Wyszukiwarki PKD w oknie modalnym ---
const PKDModal = ({ onSelect, onClose }: { onSelect: (pkd: any) => void, onClose: () => void }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    useEffect(() => {
        if (query.length < 1) { setResults([]); return; }
        const fetchPKD = async () => {
            try {
                const response = await fetch(`/api/pkd/search?q=${query}`);
                const data = await response.json();
                setResults(data);
                trackPKDSearch({
                    query,
                    resultsCount: data.length,
                    selected: false
                });
            } catch (error) { 
                console.error("Błąd podczas wyszukiwania PKD:", error);
                trackError(error as Error, 'pkd_search');
            }
        };
        const debounce = setTimeout(() => fetchPKD(), 300);
        return () => clearTimeout(debounce);
    }, [query]);
    const handleSelect = (pkd: any) => { 
        trackPKDSearch({
            query,
            resultsCount: results.length,
            selected: true
        });
        onSelect(pkd); 
        onClose(); 
    };
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-start pt-20 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center"><h3 className="text-lg font-bold text-slate-900">Wyszukaj Kod PKD</h3><button onClick={onClose} className="text-slate-500 hover:text-slate-800 transition-colors" aria-label="Zamknij wyszukiwarkę PKD"><IconX size={24} /></button></div>
                <div className="p-4"><input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Wpisz kod lub nazwę działalności..." className="w-full p-3.5 border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0 focus:outline-none" autoFocus /></div>
                <div className="flex-grow overflow-y-auto">
                    {results.length > 0 ? (
                        <ul>
                            {results.map(pkd => (
                                <li key={pkd._id} className="border-t border-slate-100">
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(pkd)}
                                        className="w-full text-left p-4 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none flex flex-col transition-colors"
                                    >
                                        <span className="font-semibold text-slate-900">{pkd.pkd_2007_code}</span>
                                        <span className="mt-1 text-sm text-slate-600">{pkd.pkd_2007_description}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-slate-500 p-8">Wpisz co najmniej 1 znak, aby rozpocząć wyszukiwanie.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Komponent Czatbota z pełną logiką ---
const AccountantChatbot = ({ token, onTokenExpired }: { token: string | null, onTokenExpired?: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<{sender: 'user' | 'bot', text: string}[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [aiLimits, setAiLimits] = useState<{used: number, total: number | string} | null>(null);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
    useEffect(scrollToBottom, [messages]);
    
    // Obsługa kliknięć w linki dashboard - delegacja zdarzeń
    useEffect(() => {
        const handleDashboardLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a[data-dashboard-subscription="true"], a[href*="dashboard"]');
            
            if (link) {
                const href = link.getAttribute('href');
                if (href === '/#dashboard' || href === '#dashboard' || link.hasAttribute('data-dashboard-subscription')) {
                    e.preventDefault();
                    e.stopPropagation();
                    localStorage.setItem('showSubscription', 'true');
                    
                    const currentHash = window.location.hash;
                    if (currentHash === '#dashboard') {
                        // Jeśli już jesteśmy na dashboard, wywołaj event natychmiast
                        window.dispatchEvent(new Event('show-subscription'));
                    } else {
                        // Przejdź do dashboard i wywołaj event po małym opóźnieniu
                        window.history.pushState({}, '', '/#dashboard');
                        window.dispatchEvent(new Event('hashchange'));
                        setTimeout(() => {
                            window.dispatchEvent(new Event('show-subscription'));
                        }, 300);
                    }
                }
            }
        };
        
        if (isOpen) {
            // Użyj delegacji zdarzeń na całym dokumencie dla tego chatbota
            document.addEventListener('click', handleDashboardLinkClick, true);
            return () => {
                document.removeEventListener('click', handleDashboardLinkClick, true);
            };
        }
    }, [isOpen, messages]);
    
    // Pobierz limity AI przy otwarciu chatbota
    useEffect(() => {
        if (isOpen && token) {
            fetch('/api/subscription/status', {
                headers: { 'x-auth-token': btoa(token) }
            })
            .then(async res => {
                if (!res.ok) {
                    if (res.status === 401) {
                        const errorData = await res.json().catch(() => ({}));
                        if (errorData.expired && onTokenExpired) {
                            onTokenExpired();
                        }
                    }
                    return null;
                }
                return res.json();
            })
            .then(data => {
                if (data && data.limits) {
                    const total = data.limits.aiQueriesPerDay === Infinity ? 'Nielimitowane' : data.limits.aiQueriesPerDay;
                    setAiLimits({
                        used: data.limits.aiQueriesUsed || 0,
                        total: total
                    });
                }
            })
            .catch(err => console.error('Błąd pobierania limitów AI:', err));
        }
    }, [isOpen, token]);
    useEffect(() => {
        if (isOpen) {
            if (token) {
                const fetchHistory = async () => {
                    try {
                        const response = await fetch('/api/chatbot/history', { headers: { 'x-auth-token': btoa(token) } });
                        const data = await response.json();
                        if (data && data.length > 0) { 
                            // Mapuj dane z bazy do formatu komponentu
                            const mappedMessages = data.map((msg: any) => ({
                                sender: msg.sender,
                                text: msg.text || msg.message
                            }));
                            setMessages(mappedMessages); 
                        } else { 
                            setMessages([{ sender: 'bot', text: 'Cześć! Jestem Twoim wirtualnym asystentem. Jak mogę pomóc?' }]); 
                        }
                    } catch (error) {
                        console.error('Błąd pobierania historii czatu:', error);
                        setMessages([{ sender: 'bot', text: 'Cześć! Jestem Twoim wirtualnym asystentem. Jak mogę pomóc?' }]);
                    }
                };
                fetchHistory();
            } else {
                setMessages([{ sender: 'bot', text: 'Cześć! Jestem Twoim wirtualnym asystentem. Zaloguj się, aby zapisać historię naszej rozmowy.' }]);
            }
        }
    }, [isOpen, token]);
    const handleSend = async (question: string = input) => {
        if (!question.trim() || isLoading) return;
        const newMessages = [...messages, { sender: 'user' as 'user', text: question }];
        setMessages(newMessages); setInput(''); setIsLoading(true);
        
        const startTime = Date.now();
        const isFirstMessage = messages.length === 0;
        
        // Track chat message
        trackChatMessage({
            messageLength: question.length,
            isFirstMessage,
        });
        
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token && typeof token === 'string') { 
                headers['x-auth-token'] = btoa(token); 
            }
            const response = await fetch('/api/ai-chat', {
                method: 'POST',
                headers,
                body: JSON.stringify({ 
                    message: question,
                    taxForm: 'skala',
                    pkdCode: '62.01.Z'
                })
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Błąd serwera AI' }));
                
                // Sprawdź czy to błąd limitu (429)
                if (response.status === 429) {
                    const limit = errorData.limit || 5;
                    let hoursUntilReset = 24;
                    
                    if (errorData.resetAt) {
                        const resetAt = new Date(errorData.resetAt);
                        const now = new Date();
                        const diffMs = resetAt.getTime() - now.getTime();
                        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
                        
                        // Jeśli resetAt jest w przeszłości lub bardzo blisko, ustaw na 24h
                        if (diffHours <= 0 || diffHours > 24) {
                            hoursUntilReset = 24;
                        } else {
                            hoursUntilReset = diffHours;
                        }
                    }
                    
                    // Utwórz ładny komunikat HTML
                    const limitMessage = `
## ⚠️ Osiągnięto limit zapytań AI

Wykorzystałeś wszystkie **${limit}** dostępnych zapytań AI w ciągu 24 godzin.

Limit odnowi się za **${hoursUntilReset} godzin**.

### 💡 Chcesz więcej zapytań?

[Przejdź na plan PRO lub EXPERT →](/dashboard)
                    `.trim();
                    setMessages([...newMessages, { sender: 'bot' as 'bot', text: limitMessage }]);
                    return;
                }
                
                throw new Error(errorData.error || 'Błąd serwera AI');
            }
            const data = await response.json();
            setMessages([...newMessages, { sender: 'bot' as 'bot', text: data.response }]);
            
            // Zaktualizuj limity po wysłaniu wiadomości
            if (token) {
                fetch('/api/subscription/status', {
                    headers: { 'x-auth-token': btoa(token) }
                })
                .then(async res => {
                    if (!res.ok) {
                        if (res.status === 401) {
                            const errorData = await res.json().catch(() => ({}));
                            if (errorData.expired && onTokenExpired) {
                                onTokenExpired();
                            }
                        }
                        return null;
                    }
                    return res.json();
                })
                .then(statusData => {
                    if (statusData && statusData.limits) {
                        const total = statusData.limits.aiQueriesPerDay === Infinity ? 'Nielimitowane' : statusData.limits.aiQueriesPerDay;
                        setAiLimits({
                            used: statusData.limits.aiQueriesUsed || 0,
                            total: total
                        });
                    }
                })
                .catch(err => console.error('Błąd aktualizacji limitów:', err));
            } else {
                // Dla niezalogowanych - szacunkowo zwiększ licznik
                if (aiLimits && typeof aiLimits.total === 'number') {
                    setAiLimits({
                        used: Math.min((aiLimits.used || 0) + 1, aiLimits.total),
                        total: aiLimits.total
                    });
                }
            }
        } catch (error) { 
            console.error("Błąd czatbota:", error);
            const errorReply = { sender: 'bot' as 'bot', text: 'Przepraszam, wystąpił błąd. Upewnij się, że klucz API dla OpenAI jest poprawnie skonfigurowany na serwerze.' };
            setMessages([...newMessages, errorReply]);
        } 
        finally { setIsLoading(false); }
    };
    return (
        <>
            {/* Przycisk chatbota - responsywny */}
            {!isOpen && (
                <button 
                    onClick={() => {
                        setIsOpen(!isOpen);
                        trackChatbotOpen();
                        sessionStorage.setItem('chatbot_session_start', Date.now().toString());
                    }} 
                    className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 bg-blue-500 text-white p-3 sm:p-4 rounded-full shadow-lg shadow-blue-500/30 hover:bg-blue-600 z-50 transition-all duration-200 touch-manipulation"
                    style={{ minWidth: '48px', minHeight: '48px' }}
                    aria-label={isOpen ? "Zamknij czat z asystentem" : "Otwórz czat z asystentem"}
                >
                    <Bot size={20} className="sm:w-6 sm:h-6" />
                </button>
            )}
            
            {/* Okienko chatbota - responsywne */}
            {isOpen && (
                <div className="fixed inset-0 sm:inset-auto sm:bottom-20 sm:right-4 sm:left-auto w-full sm:w-96 sm:max-w-md h-full sm:h-[70vh] bg-white rounded-none sm:rounded-2xl shadow-2xl flex flex-col z-40">
                    {/* Header */}
                    <header className="bg-blue-500 text-white p-3 sm:p-4 rounded-none sm:rounded-t-2xl">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-bold text-base sm:text-lg">Wirtualny Asystent AI</h3>
                            <button 
                                onClick={() => {
                                    const sessionStart = sessionStorage.getItem('chatbot_session_start');
                                    const sessionDuration = sessionStart ? (Date.now() - parseInt(sessionStart)) / 1000 : 0;
                                    setIsOpen(false);
                                    trackChatbotClose({
                                        messagesCount: messages.length,
                                        sessionDuration: Math.round(sessionDuration)
                                    });
                                    sessionStorage.removeItem('chatbot_session_start');
                                }}
                                className="p-1 hover:bg-blue-600 rounded transition-colors"
                                style={{ minWidth: '32px', minHeight: '32px' }}
                                aria-label="Zamknij okno czatu"
                            >
                                <IconX size={18} />
                            </button>
                        </div>
                        {token && aiLimits && (
                            <div className="text-xs sm:text-sm opacity-90">
                                Zapytania AI: {aiLimits.used} / {typeof aiLimits.total === 'number' ? aiLimits.total : '∞'}
                            </div>
                        )}
                        {!token && (
                            <div className="text-xs sm:text-sm opacity-90">
                                Zapytania AI (niezalogowany): 3 / 3
                            </div>
                        )}
                    </header>
                    
                    {/* Obszar wiadomości */}
                    <div className="flex-1 p-3 sm:p-4 overflow-y-auto bg-gray-50">
                {messages.map((msg, index) => (
                    <div key={index} className={`flex mb-3 sm:mb-4 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 rounded-2xl ${
                                    msg.sender === 'user' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-slate-200 text-slate-800'
                                }`}>
                                    {msg.sender === 'bot' ? (
                                        <div 
                                            className="text-sm ai-response"
                                            dangerouslySetInnerHTML={{ 
                                                __html: formatAIResponseToHTML(msg.text)
                                            }}
                                        />
                                    ) : (
                                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                                    )}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex justify-start mb-3 sm:mb-4">
                        <div className="bg-slate-200 text-slate-800 px-3 sm:px-4 py-2 rounded-2xl">
                            <div className="flex items-center">
                                        <span className="text-sm">Myślę...</span>
                                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-500 ml-2"></div>
                            </div>
                        </div>
                    </div>
                )}
                        <div ref={messagesEndRef} />
            </div>
                    
                    {/* Pole wprowadzania */}
                    <div className="p-3 sm:p-4 border-t bg-white">
                        <div className="flex items-center gap-2">
                            <input 
                                type="text" 
                                value={input} 
                                onChange={(e) => setInput(e.target.value)} 
                                onKeyPress={(e) => e.key === 'Enter' && handleSend()} 
                                placeholder="Wpisz swoje pytanie..." 
                                className="flex-1 p-3 sm:p-2 border rounded-xl text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button 
                                onClick={() => handleSend()} 
                                disabled={isLoading}
                                className="bg-blue-500 text-white p-3 sm:p-2 rounded-xl hover:bg-blue-600 disabled:opacity-50 transition-colors touch-manipulation"
                                style={{ minWidth: '44px', minHeight: '44px' }}
                            >
                                <Send size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// --- Nowy Komponent AI Assistant z OpenAI ---
const AIAssistant = ({ token }: { token: string | null }) => {
    const { showNotification } = useNotification();
    const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant', content: string, timestamp: Date }>>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [analysisData, setAnalysisData] = useState<any>(null);
    
    // Obsługa kliknięć w linki dashboard - delegacja zdarzeń dla AIAssistant
    useEffect(() => {
        const handleDashboardLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a[data-dashboard-subscription="true"], a[href*="dashboard"]');
            
            if (link) {
                const href = link.getAttribute('href');
                if (href === '/#dashboard' || href === '#dashboard' || link.hasAttribute('data-dashboard-subscription')) {
                    e.preventDefault();
                    e.stopPropagation();
                    localStorage.setItem('showSubscription', 'true');
                    
                    const currentHash = window.location.hash;
                    if (currentHash === '#dashboard') {
                        window.dispatchEvent(new Event('show-subscription'));
                    } else {
                        window.history.pushState({}, '', '/#dashboard');
                        window.dispatchEvent(new Event('hashchange'));
                        setTimeout(() => {
                            window.dispatchEvent(new Event('show-subscription'));
                        }, 300);
                    }
                }
            }
        };
        
        // Dodaj listener na cały dokument (będzie działał gdy AI panel jest otwarty)
        document.addEventListener('click', handleDashboardLinkClick, true);
        return () => {
            document.removeEventListener('click', handleDashboardLinkClick, true);
        };
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || !token) return;
        
        const startTime = Date.now();
        const isFirstMessage = messages.length === 0;
        const question = input;
        
        const userMessage = { role: 'user' as const, content: input, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        
        // Track chat message
        trackChatMessage({
            messageLength: question.length,
            isFirstMessage,
        });
        
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token && typeof token === 'string') { 
                headers['x-auth-token'] = btoa(token); 
            }
            
            // Używamy tylko OpenAI
            const response = await fetch('/api/ai-chat', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ 
                    message: input,
                    taxForm: 'skala',
                    pkdCode: '62.01.Z'
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Błąd serwera AI' }));
                
                // Sprawdź czy to błąd limitu (429)
                if (response.status === 429) {
                    const limit = errorData.limit || 5;
                    let hoursUntilReset = 24;
                    
                    if (errorData.resetAt) {
                        const resetAt = new Date(errorData.resetAt);
                        const now = new Date();
                        const diffMs = resetAt.getTime() - now.getTime();
                        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
                        
                        // Jeśli resetAt jest w przeszłości lub bardzo blisko, ustaw na 24h
                        if (diffHours <= 0 || diffHours > 24) {
                            hoursUntilReset = 24;
                        } else {
                            hoursUntilReset = diffHours;
                        }
                    }
                    
                    const limitMessage = `
## ⚠️ Osiągnięto limit zapytań AI

Wykorzystałeś wszystkie **${limit}** dostępnych zapytań AI w ciągu 24 godzin.

Limit odnowi się za **${hoursUntilReset} godzin**.

### 💡 Chcesz więcej zapytań?

[Przejdź na plan PRO lub EXPERT →](/dashboard)
                    `.trim();
                    const limitMsg = { role: 'assistant' as const, content: limitMessage, timestamp: new Date() };
                    setMessages(prev => [...prev, limitMsg]);
                    setInput('');
                    return;
                }
                
                throw new Error(errorData.error || 'Błąd serwera');
            }
            
            const data = await response.json();
            const responseTime = Date.now() - startTime;
            const aiMessage = { 
                role: 'assistant' as const, 
                content: data.response, 
                timestamp: new Date() 
            };
            setMessages(prev => [...prev, aiMessage]);
            
            // Track successful response
            trackChatMessage({
                messageLength: question.length,
                isFirstMessage,
                responseTime,
                hasError: false,
            });
        } catch (error: any) {
            console.error('Błąd:', error);
            const responseTime = Date.now() - startTime;
            const errorMessage = { 
                role: 'assistant' as const, 
                content: error.message?.includes('limit') 
                    ? error.message 
                    : 'Przepraszam, wystąpił błąd. Spróbuj ponownie za chwilę.', 
                timestamp: new Date() 
            };
            setMessages(prev => [...prev, errorMessage]);
            
            // Track error
            trackChatMessage({
                messageLength: question.length,
                isFirstMessage,
                responseTime,
                hasError: true,
            });
            trackError(error as Error, 'chatbot');
        } finally {
            setIsLoading(false);
            setInput('');
        }
    };

    const [showAnalysisForm, setShowAnalysisForm] = useState(false);
    const [isPkdModalOpen, setIsPkdModalOpen] = useState(false);
    const [analysisFormData, setAnalysisFormData] = useState({
        income: '',
        costs: '',
        taxForm: '',
        pkdCode: '',
        pkdDescription: '',
        zusType: '',
        isVatPayer: false
    });

    const handleAnalysisStart = () => {
        setShowAnalysisForm(true);
        setAnalysisData(null);
    };

    const handleAnalysis = async () => {
        if (!token) return;
        
        // Walidacja formularza
        if (!analysisFormData.income || !analysisFormData.costs || !analysisFormData.taxForm || !analysisFormData.pkdCode || !analysisFormData.zusType) {
            showNotification({
                type: 'warning',
                title: 'Brakujące dane',
                message: 'Proszę wypełnić wszystkie wymagane pola'
            });
            return;
        }
        
        setIsLoading(true);
        const startTime = Date.now();
        
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token && typeof token === 'string') { 
                headers['x-auth-token'] = btoa(token); 
            }
            
            const response = await fetch('/api/ai-analysis', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({ 
                    income: parseFloat(analysisFormData.income),
                    costs: parseFloat(analysisFormData.costs),
                    taxForm: analysisFormData.taxForm,
                    pkdCode: analysisFormData.pkdCode,
                    zusType: analysisFormData.zusType,
                    isVatPayer: analysisFormData.isVatPayer
                })
            });
            
            if (!response.ok) throw new Error('Błąd serwera');
            
            const data = await response.json();
            const analysisTime = Date.now() - startTime;
            setAnalysisData(data);
            setShowAnalysisForm(false);
            
            // Track AI analysis
            trackAIAnalysis({
                income: parseFloat(analysisFormData.income),
                costs: parseFloat(analysisFormData.costs),
                taxForm: analysisFormData.taxForm,
                analysisTime
            });
        } catch (error) {
            console.error('Błąd analizy:', error);
            trackError(error as Error, 'ai_analysis');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50 p-6 sm:p-8">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-slate-900">🤖 Asystent AI Podatkowy</h2>
                    <p className="text-slate-600 text-sm mt-2">Zadaj pytania dotyczące podatków i działalności gospodarczej</p>
                </div>

                {/* Analiza Podatkowa */}
                <div className="mb-6 p-5 sm:p-6 bg-blue-50 rounded-2xl border border-blue-100">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">📊 Analiza Podatkowa</h3>
                    <p className="text-slate-600 mb-4 text-sm">Przeanalizuj swoją sytuację podatkową z pomocą AI</p>
                    {!showAnalysisForm ? (
                        <button
                            onClick={handleAnalysisStart}
                            disabled={isLoading}
                            className="bg-blue-500 text-white px-5 py-2.5 rounded-2xl font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-lg shadow-blue-500/20"
                        >
                            Rozpocznij Analizę
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Przychód roczny (PLN)</label>
                                <input
                                    type="number"
                                    value={analysisFormData.income}
                                    onChange={(e) => setAnalysisFormData({...analysisFormData, income: e.target.value})}
                                    className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                                    placeholder="np. 100000"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Koszty roczne (PLN)</label>
                                <input
                                    type="number"
                                    value={analysisFormData.costs}
                                    onChange={(e) => setAnalysisFormData({...analysisFormData, costs: e.target.value})}
                                    className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                                    placeholder="np. 30000"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod PKD</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={analysisFormData.pkdCode ? `${analysisFormData.pkdCode}${analysisFormData.pkdDescription ? ` - ${analysisFormData.pkdDescription}` : ''}` : ''}
                                        readOnly
                                        className="flex-1 p-3.5 bg-slate-50 border-2 border-slate-100 rounded-l-2xl text-slate-800"
                                        placeholder="Wybierz kod PKD"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsPkdModalOpen(true)}
                                        className="p-3.5 bg-blue-500 text-white rounded-r-2xl hover:bg-blue-600 transition"
                                        aria-label="Wyszukaj kod PKD"
                                    >
                                        <Search size={20} />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Forma opodatkowania</label>
                                <select
                                    value={analysisFormData.taxForm}
                                    onChange={(e) => setAnalysisFormData({...analysisFormData, taxForm: e.target.value})}
                                    className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                                >
                                    <option value="">Wybierz formę...</option>
                                    <option value="skala">Skala Podatkowa</option>
                                    <option value="ryczalt">Ryczałt Ewidencjonowany</option>
                                    <option value="liniowy">Podatek Liniowy 19%</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Składki ZUS</label>
                                <select
                                    value={analysisFormData.zusType}
                                    onChange={(e) => setAnalysisFormData({...analysisFormData, zusType: e.target.value})}
                                    className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                                >
                                    <option value="">Wybierz ZUS...</option>
                                    <option value="duzy">Duży ZUS</option>
                                    <option value="maly">Mały ZUS</option>
                                    <option value="preferencyjny">Preferencyjny ZUS</option>
                                </select>
                            </div>
                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={analysisFormData.isVatPayer}
                                        onChange={(e) => setAnalysisFormData({...analysisFormData, isVatPayer: e.target.checked})}
                                        className="rounded text-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">Jestem płatnikiem VAT</span>
                                </label>
                            </div>
                            {isPkdModalOpen && (
                                <PKDModal
                                    onSelect={(pkd) => {
                                        setAnalysisFormData({
                                            ...analysisFormData,
                                            pkdCode: pkd.pkd_2007_code,
                                            pkdDescription: pkd.pkd_2007_description
                                        });
                                        setIsPkdModalOpen(false);
                                        
                                        // Pobierz stawki ryczałtu dla wybranego PKD i automatycznie ustaw
                                        fetch(`/api/tax-rules/${pkd.pkd_2007_code}`)
                                            .then(res => res.json())
                                            .then(data => {
                                                if (data.ryczaltRules && data.ryczaltRules.length > 0) {
                                                    const hasRyczalt = data.ryczaltRules.some((r: any) => r.allowed);
                                                    if (hasRyczalt) {
                                                        setAnalysisFormData(prev => ({...prev, taxForm: 'ryczalt'}));
                                                    }
                                                }
                                            })
                                            .catch(err => console.error('Błąd pobierania stawek ryczałtu:', err));
                                    }}
                                    onClose={() => setIsPkdModalOpen(false)}
                                />
                            )}
                            <div className="flex gap-2">
                                <button
                                    onClick={handleAnalysis}
                                    disabled={isLoading}
                                    className="flex-1 bg-blue-500 text-white px-5 py-2.5 rounded-2xl font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-lg shadow-blue-500/20"
                                >
                                    {isLoading ? 'Analizuję...' : 'Przeprowadź Analizę'}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAnalysisForm(false);
                                        setIsPkdModalOpen(false);
                                        setAnalysisFormData({
                                            income: '',
                                            costs: '',
                                            taxForm: '',
                                            pkdCode: '',
                                            pkdDescription: '',
                                            zusType: '',
                                            isVatPayer: false
                                        });
                                    }}
                                    className="px-5 py-2.5 rounded-2xl font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                                >
                                    Anuluj
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {analysisData && (
                        <div className="mt-4 p-5 sm:p-6 bg-white rounded-2xl border-2 border-slate-100 shadow-sm">
                            <h4 className="font-bold mb-4 text-lg text-slate-900 flex items-center gap-2">
                                <span className="w-1 h-6 bg-blue-500 rounded-full"></span>
                                Wyniki Analizy
                            </h4>
                            <div 
                                className="text-sm sm:text-base text-slate-700"
                                dangerouslySetInnerHTML={{ 
                                    __html: formatAIResponseToHTML(analysisData.analysis)
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Chat */}
                <div className="border-2 border-slate-100 rounded-2xl h-80 sm:h-96 flex flex-col overflow-hidden">
                    <div className="flex-1 p-4 overflow-y-auto bg-slate-50/50">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex mb-3 sm:mb-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl ${
                                    msg.role === 'user' 
                                        ? 'bg-blue-500 text-white shadow-md' 
                                        : 'bg-white border border-slate-200 text-slate-800'
                                }`}>
                                    {msg.role === 'assistant' ? (
                                        <div 
                                            className="text-xs sm:text-sm max-w-none ai-response"
                                            dangerouslySetInnerHTML={{ 
                                                __html: formatAIResponseToHTML(msg.content)
                                            }}
                                        />
                                    ) : (
                                        <p className="text-xs sm:text-sm whitespace-pre-wrap">{msg.content}</p>
                                    )}
                                    <p className="text-xs opacity-70 mt-1">
                                        {msg.timestamp.toLocaleTimeString()}
                                    </p>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start mb-3 sm:mb-4">
                                <div className="bg-white border border-slate-200 text-slate-800 px-4 py-2.5 rounded-2xl">
                                    <div className="flex items-center">
                                        <span className="text-xs sm:text-sm">Myślę...</span>
                                        <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-500 ml-2"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="p-4 border-t border-slate-100 bg-white">
                        <div className="flex items-center">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                                placeholder="Zadaj pytanie o podatki..."
                                className="flex-1 p-3.5 border-2 border-slate-100 rounded-l-2xl text-slate-900 focus:border-blue-500 focus:ring-0 focus:outline-none"
                            />
                            <button
                                onClick={handleSend}
                                disabled={isLoading}
                                className="bg-blue-500 text-white p-3.5 rounded-r-2xl hover:bg-blue-600 disabled:opacity-50 font-semibold transition-colors"
                            >
                                Wyślij
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Komponent Porównania Form Opodatkowania ---
const TaxFormComparison = ({ token, onUpgrade }: { token: string | null, onUpgrade?: (plan: 'pro' | 'expert') => void }) => {
    const { showNotification, showConfirm } = useNotification();
    const [comparisonData, setComparisonData] = useState<any[]>([]);
    const [isComparing, setIsComparing] = useState(false);
    const [comparisonError, setComparisonError] = useState('');
    const [businessSectors, setBusinessSectors] = useState<any[]>([]);
    const [selectedSector, setSelectedSector] = useState<any>(null);
    const [vatRates, setVatRates] = useState<any[]>([]);
    const [selectedVatRate, setSelectedVatRate] = useState<number | null>(null);
    const [useSuggestedVat, setUseSuggestedVat] = useState<boolean>(true);
    const [isPkdModalOpen, setIsPkdModalOpen] = useState(false);
    const [selectedPkdText, setSelectedPkdText] = useState("Kliknij lupę, aby wyszukać...");
    const [ryczaltOptions, setRyczaltOptions] = useState<any[]>([]);
    const [vatWarning, setVatWarning] = useState('');
    const [ryczaltRates, setRyczaltRates] = useState<any[]>([]);
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);

    // Pobierz branże, stawki VAT i stawki ryczałtu przy ładowaniu komponentu
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Pobierz branże
                const sectorsResponse = await fetch('/api/business-sectors');
                if (sectorsResponse.ok) {
                    const sectors = await sectorsResponse.json();
                    setBusinessSectors(sectors);
                }
                
                // Pobierz stawki VAT
                const vatRatesResponse = await fetch('/api/vat-rates');
                if (vatRatesResponse.ok) {
                    const rates = await vatRatesResponse.json();
                    setVatRates(rates);
                }

                // Pobierz stawki ryczałtu
                const ryczaltRatesResponse = await fetch('/api/ryczalt-rates');
                if (ryczaltRatesResponse.ok) {
                    const rates = await ryczaltRatesResponse.json();
                    setRyczaltRates(rates);
                }
            } catch (error) {
                console.error('Błąd pobierania danych:', error);
            }
        };
        fetchData();
        
        // Pobierz status subskrypcji
        if (token) {
            fetch('/api/subscription/status', {
                headers: { 'x-auth-token': btoa(token) }
            })
            .then(res => res.json())
            .then(data => setSubscriptionStatus(data))
            .catch(err => console.error('Błąd pobierania statusu subskrypcji:', err));
        }
    }, [token]);

    // Automatyczne dostosowanie parametrów na podstawie wybranej branży
    const handleSectorChange = (sector: any, setFieldValue: any) => {
        setSelectedSector(sector);
        if (sector) {
            // Automatycznie ustaw status VAT na podstawie branży
            const isVatExempt = sector.isVatExempt;
            // Jeśli branża jest zwolniona z VAT, automatycznie ustaw na zwolniony
            if (isVatExempt) {
                setFieldValue('isVatPayer', 'false');
                setUseSuggestedVat(true);
                setSelectedVatRate(null);
            } else {
                // Jeśli branża nie jest zwolniona, ustaw sugerowaną stawkę VAT
                setFieldValue('isVatPayer', 'true');
                setUseSuggestedVat(true);
                setSelectedVatRate(sector.defaultVatRate);
            }
            
            // Automatycznie ustaw stawkę ryczałtu na podstawie branży
            if (sector.defaultRyczaltRate) {
                setFieldValue('ryczaltRate', sector.defaultRyczaltRate);
            }
        }
    };

    const handlePkdSelect = async (pkd: any, setFieldValue: Function) => {
        setFieldValue('pkd', pkd.pkd_2007_code); 
        setSelectedPkdText(`${pkd.pkd_2007_code} - ${pkd.pkd_2007_description}`);
        try {
            const response = await fetch(`/api/tax-rules/${pkd.pkd_2007_code}`);
            const { vatRule, ryczaltRules } = await response.json();
            if (vatRule?.is_mandatory_vat) { 
                setFieldValue('isVatPayer', 'true'); 
                setVatWarning(vatRule.ui_warning_text); 
            } else { 
                setVatWarning(''); 
            }
            if (ryczaltRules && ryczaltRules.length > 0) {
                setRyczaltOptions(ryczaltRules);
                if (ryczaltRules.length === 1 && !ryczaltRules[0].is_ambiguous) { 
                    setFieldValue('ryczaltRate', ryczaltRules[0].rate); 
                }
            } else { 
                setRyczaltOptions([]); 
            }
        } catch (error) { 
            console.error("Błąd pobierania reguł podatkowych:", error); 
        }
    };

    const handleCompare = async (values: any) => {
        const startTime = Date.now();
        
        // Sprawdź czy użytkownik ma dostęp do porównania
        if (!token || !subscriptionStatus || (subscriptionStatus.plan !== 'pro' && subscriptionStatus.plan !== 'expert')) {
            setIsComparing(false);
            showNotification({
                type: 'warning',
                title: 'Wymagany wyższy plan',
                message: 'Porównanie form opodatkowania wymaga planu PRO lub EXPERT.',
                showGoToSubscription: true,
                onGoToSubscription: () => {
                    localStorage.setItem('showSubscription', 'true');
                    window.location.href = '/#dashboard';
                }
            });
            return;
        }
        
        setIsComparing(true);
        setComparisonData([]);
        setComparisonError('');
        
        try {
            const payload = { 
                ...values, 
                isVatPayer: values.isVatPayer === 'true',
                businessSectorId: selectedSector?._id,
                vatRate: useSuggestedVat && selectedSector ? selectedSector.defaultVatRate : selectedVatRate,
                calculationPeriod: calculationPeriod
            };
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token && typeof token === 'string') { 
                headers['x-auth-token'] = btoa(token); 
            }
            
            const response = await fetch('/api/compare-tax-forms', {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Błąd serwera');
            }
            
            const data = await response.json();
            const comparisonTime = Date.now() - startTime;
            setComparisonData(data);
            
            // Track tax form comparison
            const formsCompared = data.map((item: any) => item.taxForm || 'unknown');
            const bestForm = data.length > 0 ? (data[0].taxForm || 'unknown') : 'unknown';
            trackTaxFormComparison({
                income: parseFloat(values.income) || 0,
                costs: parseFloat(values.costs) || 0,
                formsCompared,
                bestForm
            });
        } catch (error: any) { 
            console.error("Błąd porównania:", error);
            trackError(error as Error, 'tax_form_comparison');
            setComparisonError(error.message || 'Wystąpił błąd podczas porównania');
        } 
        finally { 
            setIsComparing(false); 
        }
    };

    const [calculationPeriod, setCalculationPeriod] = useState<'monthly' | 'yearly'>('monthly');
    const initialValues = { income: 10000, costs: 1500, taxForm: 'skala', isVatPayer: 'false', vatRate: 23, zusType: 'duzy', incomeSoFar: 0, paidTaxAdvanceSoFar: 0, pkd: null, ryczaltRate: 12 };

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <Formik initialValues={initialValues} onSubmit={handleCompare} enableReinitialize>
                {({ values, setFieldValue }) => (
                    <div className="space-y-6">
                        <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                            <h2 className="text-2xl font-bold text-slate-900 mb-5 flex items-center gap-2">
                                <TrendingUp className="h-6 w-6 text-blue-500" />
                                Porównanie Form Opodatkowania
                            </h2>
                            <Form className="space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                            Przychód {calculationPeriod === 'monthly' ? '(miesięczny)' : '(roczny)'}
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <Field name="income" type="number" className="flex-1 p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0" />
                                            <div className="flex bg-slate-100 rounded-2xl p-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setCalculationPeriod('monthly')}
                                                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${
                                                        calculationPeriod === 'monthly' 
                                                            ? 'bg-white text-blue-600 shadow-sm' 
                                                            : 'text-slate-600 hover:text-slate-800'
                                                    }`}
                                                >
                                                    Miesięczny
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCalculationPeriod('yearly')}
                                                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${
                                                        calculationPeriod === 'yearly' 
                                                            ? 'bg-white text-blue-600 shadow-sm' 
                                                            : 'text-slate-600 hover:text-slate-800'
                                                    }`}
                                                >
                                                    Roczny
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Koszty</label>
                                        <Field name="costs" type="number" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0" />
                                    </div>
                                </div>
                                
                                {/* Wybór kodu PKD */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod PKD (opcjonalnie)</label>
                                    <div className="flex items-center">
                                        <input 
                                            type="text" 
                                            readOnly 
                                            value={selectedPkdText} 
                                            className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-l-2xl cursor-default text-slate-800" 
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setIsPkdModalOpen(true)} 
                                            className="p-3.5 bg-blue-500 text-white rounded-r-2xl hover:bg-blue-600 transition"
                                        >
                                            <Search size={20} />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Wybór branży działalności */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Branża działalności (opcjonalnie)</label>
                                    <select 
                                        value={selectedSector?._id || ''} 
                                        onChange={(e) => {
                                            const sector = businessSectors.find(s => s._id === e.target.value);
                                            handleSectorChange(sector || null, setFieldValue);
                                        }}
                                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                                    >
                                        <option value="">Wybierz branżę...</option>
                                        {businessSectors.map(sector => (
                                            <option key={sector._id} value={sector._id}>
                                                {sector.name} - {sector.description}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedSector && (
                                        <div className="mt-2 p-4 bg-blue-50 rounded-2xl text-sm border border-blue-100">
                                            <p><strong>Wybrana branża:</strong> {selectedSector.name}</p>
                                            <p><strong>Kod PKD:</strong> {selectedSector.pkdCode}</p>
                                            <p><strong>VAT:</strong> {selectedSector.isVatExempt ? 'Zwolniony' : `Czynny (${selectedSector.defaultVatRate}%)`}</p>
                                            <p><strong>Ryczałt:</strong> {selectedSector.defaultRyczaltRate}%</p>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status VAT</label>
                                        <Field as="select" name="isVatPayer" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0">
                                            <option value="false">Zwolniony</option>
                                            <option value="true">Czynny</option>
                                        </Field>
                                        {selectedSector && (
                                            <div className="mt-2 p-2 bg-yellow-50 rounded-xl text-xs text-yellow-800 border border-yellow-100">
                                                <strong>Sugerowane:</strong> {selectedSector.isVatExempt ? 'Zwolniony' : 'Czynny'}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Stawka VAT</label>
                                        {values.isVatPayer === 'true' ? (
                                            <div className="space-y-2">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="suggested-vat"
                                                        name="vatRateType"
                                                        checked={useSuggestedVat}
                                                        onChange={() => {
                                                            setUseSuggestedVat(true);
                                                            setSelectedVatRate(selectedSector ? selectedSector.defaultVatRate : 23);
                                                        }}
                                                        className="text-blue-500"
                                                    />
                                                    <label htmlFor="suggested-vat" className="text-sm text-slate-700">
                                                        Sugerowana z branży ({selectedSector ? `${selectedSector.defaultVatRate}%` : '23%'})
                                                    </label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="radio"
                                                        id="manual-vat"
                                                        name="vatRateType"
                                                        checked={!useSuggestedVat}
                                                        onChange={() => setUseSuggestedVat(false)}
                                                        className="text-blue-500"
                                                    />
                                                    <label htmlFor="manual-vat" className="text-sm text-slate-700">
                                                        Wybierz ręcznie
                                                    </label>
                                                </div>
                                                {!useSuggestedVat && (
                                                    <select
                                                        value={selectedVatRate || ''}
                                                        onChange={(e) => setSelectedVatRate(parseFloat(e.target.value))}
                                                        className="w-full p-3 bg-white border-2 border-slate-100 rounded-2xl text-sm text-slate-900 focus:border-blue-500 focus:ring-0"
                                                    >
                                                        <option value="">Wybierz stawkę VAT...</option>
                                                        {/* Polskie stawki VAT: 23%, 8%, 5%, 0% */}
                                                        {vatRates.map(rate => (
                                                            <option key={rate.rate} value={rate.rate}>
                                                                {rate.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="p-3 bg-slate-100 rounded-2xl text-sm text-slate-600">
                                                VAT jest zwolniony - stawka nie dotyczy
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Składki ZUS</label>
                                    <Field as="select" name="zusType" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0">
                                        <option value="duzy">Duży ZUS</option>
                                        <option value="maly">Mały ZUS (preferencyjny - nowe firmy)</option>
                                    </Field>
                                </div>
                                
                                {/* Pole stawki ryczałtu */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Stawka ryczałtu (dla porównania)</label>
                                    {ryczaltOptions.length > 1 && ryczaltOptions[0].is_ambiguous && (
                                        <div className="p-3 bg-blue-50 text-blue-800 rounded-2xl mb-2 text-sm border border-blue-100">
                                            <p className="font-semibold">Doprecyzuj rodzaj usług:</p>
                                            {ryczaltOptions.map(option => (
                                                <label key={option.rate} className="flex items-center mt-1">
                                                    <Field type="radio" name="ryczaltRate" value={String(option.rate)} className="mr-2"/>
                                                    {option.description} ({option.rate}%)
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                    <Field as="select" name="ryczaltRate" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0">
                                        {ryczaltRates.length > 0 ? (
                                            ryczaltRates.map((rate: any) => (
                                                <option key={rate.rate} value={rate.rate}>
                                                    {rate.rate}% - {rate.description}
                                                </option>
                                            ))
                                        ) : (
                                            <>
                                                <option value="17">17% - Wolne zawody</option>
                                                <option value="15">15% - Usługi pośrednictwa, doradztwa</option>
                                                <option value="14">14% - Opieka zdrowotna, usługi inżynierskie</option>
                                                <option value="12">12% - Usługi IT (oprogramowanie)</option>
                                                <option value="10">10% - Kupno i sprzedaż nieruchomości</option>
                                                <option value="8.5">8.5% - Najem, usługi ogólne</option>
                                                <option value="5.5">5.5% - Roboty budowlane, produkcja</option>
                                                <option value="3">3% - Handel, gastronomia</option>
                                                <option value="2">2% - Sprzedaż produktów rolnych</option>
                                            </>
                                        )}
                                    </Field>
                                </div>
                                
                                {vatWarning && (
                                    <div className="p-3 bg-yellow-50 text-yellow-800 rounded-2xl flex items-center border border-yellow-100">
                                        <AlertTriangle size={20} className="mr-3" />
                                        {vatWarning}
                                    </div>
                                )}
                                
                                <button type="submit" disabled={isComparing} className="w-full px-6 py-3.5 font-bold text-white bg-blue-500 rounded-2xl shadow-lg shadow-blue-500/30 disabled:bg-slate-300 disabled:shadow-none hover:bg-blue-600 transition-all">
                                    {isComparing ? 'Porównuję...' : 'Porównaj Formy Opodatkowania'}
                                </button>
                            </Form>
                        </div>

                        {comparisonError && (
                            <div className="p-3 bg-red-50 text-red-800 rounded-2xl flex items-center border border-red-100">
                                <AlertTriangle size={20} className="mr-3" />
                                {comparisonError}
                            </div>
                        )}
                        
                        {/* Modal PKD */}
                        {isPkdModalOpen && <PKDModal onSelect={(pkd) => handlePkdSelect(pkd, setFieldValue)} onClose={() => setIsPkdModalOpen(false)} />}

                        {comparisonData.length > 0 && (
                            <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                                <h3 className="text-xl font-bold text-slate-900 mb-5">Wyniki Porównania</h3>
                                {selectedSector && (
                                    <div className="mb-4 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                                        <p className="text-sm text-blue-800">
                                            <strong>Branża:</strong> {selectedSector.name} | 
                                            <strong> PKD:</strong> {selectedSector.pkdCode} | 
                                            <strong> VAT:</strong> {useSuggestedVat ? 
                                                (selectedSector.isVatExempt ? 'Zwolniony' : `${selectedSector.defaultVatRate}%`) : 
                                                `${selectedVatRate}%`
                                            }
                                        </p>
                                    </div>
                                )}
                                <div className="space-y-4">
                                    {comparisonData.map((result, index) => (
                                        <div key={index} className={`p-5 sm:p-6 rounded-2xl border-2 ${index === 0 ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
                                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
                                                <h4 className="font-bold text-lg text-slate-900">
                                                    {result.taxForm === 'skala' ? 'Skala Podatkowa' : 
                                                     result.taxForm === 'liniowy' ? 'Podatek Liniowy' : 
                                                     `Ryczałt Ewidencjonowany ${result.ryczaltRate}%`}
                                                </h4>
                                                {index === 0 && <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">NAJLEPSZE</span>}
                                            </div>
                                            {result.error ? (
                                                <p className="text-red-600">{result.error}</p>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                                    <div className="bg-white p-4 rounded-2xl border border-slate-100">
                                                        <span className="text-slate-500 block mb-1 text-xs font-bold uppercase tracking-wider">Zysk netto</span>
                                                        <p className="font-bold text-lg text-blue-600">{result.zyskNetto?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</p>
                                                    </div>
                                                    <div className="bg-white p-4 rounded-2xl border border-slate-100">
                                                        <span className="text-slate-500 block mb-1 text-xs font-bold uppercase tracking-wider">Podatek</span>
                                                        <p className="font-semibold text-red-600">{result.podatekDochodowy?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</p>
                                                    </div>
                                                    <div className="bg-white p-4 rounded-2xl border border-slate-100">
                                                        <span className="text-slate-500 block mb-1 text-xs font-bold uppercase tracking-wider">ZUS (społeczne + zdrowotne)</span>
                                                        <p className="font-semibold text-orange-600">{(result.zusSpoleczne + result.zusZdrowotne)?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</p>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            ZUS społeczne: {result.zusSpoleczne?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })} | 
                                                            Zdrowotne: {result.zusZdrowotne?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                                                        </p>
                                                    </div>
                                                    <div className="bg-white p-4 rounded-2xl border border-slate-100">
                                                        <span className="text-slate-500 block mb-1 text-xs font-bold uppercase tracking-wider">Ulgi</span>
                                                        <p className="font-semibold text-blue-600">{result.ulgiPodatkowe?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Formik>
        </div>
    );
};

// --- Komponent Kalkulatora ---
const CalculatorComponent = ({ token, onUpgrade }: { token: string | null, onUpgrade?: (plan: 'pro' | 'expert') => void }) => {
    const { showNotification, showConfirm } = useNotification();
    const [results, setResults] = useState<any>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [vatWarning, setVatWarning] = useState('');
    const [calculationError, setCalculationError] = useState('');
    const [ryczaltOptions, setRyczaltOptions] = useState<any[]>([]);
    const [isPkdModalOpen, setIsPkdModalOpen] = useState(false);
    const [selectedPkdText, setSelectedPkdText] = useState("Kliknij lupę, aby wyszukać...");
    const [appData, setAppData] = useState<any>({ vatRates: [], ryczaltRates: [] });
    const [calculationMode, setCalculationMode] = useState<'grossToNet' | 'netToGross'>('grossToNet');
    const [calculationPeriod, setCalculationPeriod] = useState<'monthly' | 'yearly'>('monthly');
    const [subscriptionLimits, setSubscriptionLimits] = useState<any>(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await fetch('/api/initial-data');
                const data = await response.json();
                setAppData(data);
            } catch (error) { console.error("Błąd pobierania danych inicjalizacyjnych:", error); }
        };
        fetchInitialData();
        
        // Pobierz status subskrypcji jeśli użytkownik jest zalogowany
        if (token) {
            fetch('/api/subscription/status', {
                headers: { 'x-auth-token': btoa(token) }
            })
            .then(async res => {
                if (!res.ok) {
                    if (res.status === 401) {
                        const errorData = await res.json().catch(() => ({}));
                        if (errorData.expired) {
                            localStorage.removeItem('token');
                            window.dispatchEvent(new CustomEvent('token-expired'));
                        }
                    }
                    if (res.status === 429) {
                        return null; // Pomiń rate limit, nie loguj błędu
                    }
                    return null;
                }
                return res.json();
            })
            .then(data => {
                if (data) setSubscriptionLimits(data);
            })
            .catch(err => {
                // Tylko loguj błędy nie-rate-limit
                if (!err.message?.includes('429')) {
                    console.error('Błąd pobierania statusu subskrypcji:', err);
                }
            });
        }
    }, [token]);

    const handleSubmit = async (values: any) => {
        setIsCalculating(true); 
        setResults(null);
        setCalculationError('');
        
        const startTime = Date.now();
        
        try {
            const payload = { ...values, calculationMode, isVatPayer: values.isVatPayer === 'true', calculationPeriod };
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token && typeof token === 'string') { 
                headers['x-auth-token'] = btoa(token); 
            }
            const response = await fetch('/api/calculate', {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Błąd serwera');
            }
            
            const data = await response.json();
            const calculationTime = Date.now() - startTime;
            setResults(data);
            
            // Track calculation
            trackCalculation({
                income: parseFloat(values.income) || 0,
                costs: parseFloat(values.costs) || 0,
                taxForm: values.taxForm || 'unknown',
                zusType: values.zusType || 'unknown',
                pkdCode: values.pkd?.pkd_2007_code || values.pkdCode || undefined,
                isVatPayer: values.isVatPayer === 'true',
                calculationTime
            });
        } catch (error: any) { 
            console.error("Błąd kalkulacji:", error);
            setCalculationError(error.message || 'Wystąpił błąd podczas obliczania podatków');
            trackError(error as Error, 'calculation');
        } 
        finally { 
            setIsCalculating(false); 
        }
    };

    const handleSaveCalculation = async (values: any, calcResults: any) => {
        console.log('=== ROZPOCZYNANIE ZAPISYWANIA KALKULACJI ===');
        console.log('Token:', token);
        console.log('Values:', values);
        console.log('CalcResults:', calcResults);
        console.log('CalcResults type:', typeof calcResults);
        console.log('CalcResults keys:', calcResults ? Object.keys(calcResults) : 'null/undefined');
        
        if (!token) { 
            console.log('Brak tokenu - przerywanie');
            showNotification({
                type: 'info',
                title: 'Wymagane logowanie',
                message: 'Zaloguj się, aby zapisać obliczenie.'
            });
            return; 
        }
        if (!calcResults) { 
            console.log('calcResults jest null/undefined:', calcResults);
            showNotification({
                type: 'warning',
                title: 'Brak wyników',
                message: 'Brak wyników do zapisania. Najpierw wykonaj obliczenia.'
            });
            return; 
        }
        if (!values) {
            console.log('values jest null/undefined:', values);
            showNotification({
                type: 'warning',
                title: 'Brak danych',
                message: 'Brak danych wejściowych do zapisania.'
            });
            return;
        }
        
        // Sprawdź czy calcResults ma wymagane właściwości
        if (typeof calcResults !== 'object' || Object.keys(calcResults).length === 0) {
            console.log('calcResults nie jest obiektem lub jest pusty:', calcResults);
            showNotification({
                type: 'error',
                title: 'Nieprawidłowe wyniki',
                message: 'Wyniki obliczeń są nieprawidłowe. Wykonaj obliczenia ponownie.'
            });
            return;
        }
        
        try {
            console.log('=== ZAPISYWANIE KALKULACJI ===');
            console.log('Token:', token);
            console.log('Token type:', typeof token);
            console.log('Token length:', token?.length);
            
            // Sprawdź czy token jest prawidłowy
            if (!token || typeof token !== 'string' || token.length < 10) {
                showNotification({
                    type: 'error',
                    title: 'Nieprawidłowy token',
                    message: 'Token jest nieprawidłowy. Zaloguj się ponownie.'
                });
                return;
            }
            
            console.log('Token (base64):', btoa(token));
            console.log('Values:', values);
            console.log('CalcResults:', calcResults);
            console.log('Request body:', JSON.stringify({ inputs: values, outputs: calcResults }));
            
            const response = await fetch('/api/calculations', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'x-auth-token': btoa(token) 
                },
                body: JSON.stringify({ inputs: values, outputs: calcResults })
            });
            
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);
            
            const result = await response.json();
            console.log('Response result:', result);
            
            if (response.ok && result.success) {
                showNotification({
                    type: 'success',
                    title: 'Zapisano!',
                    message: 'Obliczenie zostało zapisane w Twoim panelu!'
                });
                console.log('Kalkulacja zapisana:', result.calculation);
            } else {
                console.error('Błąd zapisu - szczegóły:', {
                    status: response.status,
                    statusText: response.statusText,
                    result: result
                });
                showNotification({
                    type: 'error',
                    title: 'Błąd zapisu',
                    message: `Błąd zapisywania (${response.status}): ${result.error || result.msg || 'Nieznany błąd'}`
                });
            }
        } catch (error) { 
            console.error('Błąd zapisu obliczenia:', error); 
            showNotification({
                type: 'error',
                title: 'Błąd połączenia',
                message: 'Błąd połączenia z serwerem. Sprawdź konsolę.'
            });
        }
    };

    const initialValues = { income: 10000, targetNet: 5000, costs: 1500, taxForm: 'skala', ryczaltRate: 12, isVatPayer: 'false', vatRate: 23, zusType: 'duzy', incomeSoFar: 0, paidTaxAdvanceSoFar: 0, pkd: null };
    const handlePkdSelect = async (pkd: any, setFieldValue: Function) => {
        setFieldValue('pkd', pkd.pkd_2007_code); setSelectedPkdText(`${pkd.pkd_2007_code} - ${pkd.pkd_2007_description}`);
        try {
            const response = await fetch(`/api/tax-rules/${pkd.pkd_2007_code}`);
            const { vatRule, ryczaltRules } = await response.json();
            if (vatRule?.is_mandatory_vat) { setFieldValue('isVatPayer', 'true'); setVatWarning(vatRule.ui_warning_text); } 
            else { setVatWarning(''); }
            if (ryczaltRules && ryczaltRules.length > 0) {
                setRyczaltOptions(ryczaltRules);
                if (ryczaltRules.length === 1 && !ryczaltRules[0].is_ambiguous) { setFieldValue('ryczaltRate', ryczaltRules[0].rate); }
            } else { setRyczaltOptions([]); }
        } catch (error) { console.error("Błąd pobierania reguł podatkowych:", error); }
    };
    const chartData = results ? [ { name: 'Przychód Netto', value: results.przychodNetto, fill: '#8884d8' }, { name: 'Koszty', value: -results.koszty, fill: '#FBBF24' }, { name: 'ZUS Społeczne', value: -results.zusSpoleczne, fill: '#F87171' }, { name: 'ZUS Zdrowotne', value: -results.zusZdrowotne, fill: '#FCA5A5' }, { name: 'Podatek', value: -results.podatekDochodowy, fill: '#FB923C' }, { name: 'Zysk "na rękę"', value: results.zyskNetto, fill: '#34D399' }, ] : [];

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                {({ values, setFieldValue }) => (
                    <>
                        {isPkdModalOpen && <PKDModal onSelect={(pkd) => handlePkdSelect(pkd, setFieldValue)} onClose={() => setIsPkdModalOpen(false)} />}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                            <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                                <Form className="space-y-5 sm:space-y-6">
                                    <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Settings className="h-6 w-6 text-blue-500" />Konfiguracja</h2>
                                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Tryb Obliczeń</label><div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 rounded-2xl"><button type="button" onClick={() => setCalculationMode('grossToNet')} className={`p-3 rounded-xl font-semibold text-sm transition ${calculationMode === 'grossToNet' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-600 hover:bg-slate-200'}`}>Przychód → Zysk</button><button 
                                        type="button" 
                                        onClick={() => {
                                            // Sprawdź czy użytkownik ma dostęp do trybu netToGross
                                            if (!token || !subscriptionLimits || (subscriptionLimits.plan !== 'pro' && subscriptionLimits.plan !== 'expert')) {
                                                showNotification({
                                                    type: 'warning',
                                                    title: 'Wymagany wyższy plan',
                                                    message: 'Tryb "Zysk → Przychód" wymaga planu PRO lub EXPERT.',
                                                    showGoToSubscription: true,
                                                    onGoToSubscription: () => {
                                                        localStorage.setItem('showSubscription', 'true');
                                                        window.location.href = '/#dashboard';
                                                    }
                                                });
                                                return;
                                            }
                                            setCalculationMode('netToGross');
                                        }} 
                                        className={`p-3 rounded-xl font-semibold text-sm transition relative ${
                                            calculationMode === 'netToGross' 
                                                ? 'bg-blue-500 text-white shadow-md' 
                                                : subscriptionLimits && (subscriptionLimits.plan === 'pro' || subscriptionLimits.plan === 'expert')
                                                ? 'text-slate-600 hover:bg-slate-200'
                                                : 'opacity-60 cursor-not-allowed bg-slate-100'
                                        }`}
                                        title={(!token || !subscriptionLimits || (subscriptionLimits.plan !== 'pro' && subscriptionLimits.plan !== 'expert')) ? 'Wymaga planu PRO/EXPERT' : ''}
                                    >
                                        Zysk → Przychód
                                        {subscriptionLimits && (subscriptionLimits.plan !== 'pro' && subscriptionLimits.plan !== 'expert') && (
                                            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 rounded-full">PRO</span>
                                        )}
                                    </button></div></div>
                                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod PKD</label><div className="flex items-center"><input type="text" readOnly value={selectedPkdText} className="w-full p-3.5 bg-slate-50 border-2 border-slate-100 rounded-l-2xl cursor-default text-slate-800 focus:border-blue-500 focus:ring-0" /><button type="button" onClick={() => setIsPkdModalOpen(true)} className="p-3.5 bg-blue-500 text-white rounded-r-2xl hover:bg-blue-600 transition"><Search size={20} /></button></div></div>
                                    {calculationMode === 'grossToNet' ? (
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                                                Przychód (kwota {values.isVatPayer === 'true' ? "brutto" : "netto"}) {calculationPeriod === 'monthly' ? '(miesięczny)' : '(roczny)'}
                                            </label>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Field name="income" type="number" className="flex-1 p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0" />
                                                <div className="flex bg-slate-100 rounded-2xl p-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCalculationPeriod('monthly')}
                                                        className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${
                                                            calculationPeriod === 'monthly' 
                                                                ? 'bg-white text-blue-600 shadow-sm' 
                                                                : 'text-slate-600 hover:text-slate-800'
                                                        }`}
                                                    >
                                                        Miesięczny
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCalculationPeriod('yearly')}
                                                        className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${
                                                            calculationPeriod === 'yearly' 
                                                                ? 'bg-white text-blue-600 shadow-sm' 
                                                                : 'text-slate-600 hover:text-slate-800'
                                                        }`}
                                                    >
                                                        Roczny
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Oczekiwany zysk „na rękę"</label>
                                            <Field name="targetNet" type="number" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0" />
                                        </div>
                                    )}
                                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Koszty</label><Field name="costs" type="number" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0" /></div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Forma opodatkowania</label><Field as="select" name="taxForm" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"><option value="skala">Skala Podatkowa</option><option value="liniowy">Podatek Liniowy</option><option value="ryczalt">Ryczałt Ewidencjonowany</option></Field></div>
                                        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Składki ZUS</label><Field as="select" name="zusType" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"><option value="duzy">Duży ZUS</option><option value="maly">Mały ZUS (preferencyjny - nowe firmy)</option></Field></div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Status VAT</label><Field as="select" name="isVatPayer" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"><option value={"false"}>Zwolniony</option><option value={"true"}>Czynny</option></Field></div>
                                        {values.isVatPayer === 'true' && (<div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Stawka VAT</label><Field as="select" name="vatRate" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0">{appData.vatRates.map((rate: any) => <option key={rate.rate} value={rate.rate}>{rate.label}</option>)}</Field></div>)}
                                    </div>
                                    {vatWarning && <div className="p-3 bg-yellow-100 text-yellow-800 rounded-lg flex items-center"><AlertTriangle size={20} className="mr-3" />{vatWarning}</div>}
                                    {calculationError && <div className="p-3 bg-red-100 text-red-800 rounded-lg flex items-center"><AlertTriangle size={20} className="mr-3" />{calculationError}</div>}
                                    {values.taxForm === 'ryczalt' && (<div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Stawka ryczałtu</label>{ryczaltOptions.length > 1 && ryczaltOptions[0].is_ambiguous && (<div className="p-3 bg-blue-50 text-blue-800 rounded-2xl mb-2 text-sm"><p className="font-semibold">Doprecyzuj rodzaj usług:</p>{ryczaltOptions.map(option => (<label key={option.rate} className="flex items-center mt-1"><Field type="radio" name="ryczaltRate" value={String(option.rate)} className="mr-2"/>{option.description} ({option.rate}%)</label>))}</div>)}<Field as="select" name="ryczaltRate" className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0">{ryczaltOptions.length > 0 ? ryczaltOptions.map(r => <option key={r.rate} value={r.rate}>{r.rate}% - {r.description}</option>) : appData.ryczaltRates.map((r: any) => <option key={r.rate} value={r.rate}>{r.rate}% - {r.description}</option>)}</Field></div>)}
                                    <div className="p-5 bg-slate-50 rounded-2xl space-y-4 border border-slate-100"><h3 className="font-semibold text-slate-800">Dane narastające (od początku roku do POPRZEDNIEGO miesiąca)</h3><div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Przychód/Dochód narastająco</label><Field name="incomeSoFar" type="number" className="w-full p-3 bg-white border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div><div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Zapłacone zaliczki na podatek</label><Field name="paidTaxAdvanceSoFar" type="number" className="w-full p-3 bg-white border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div></div>
                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-4 space-y-3 sm:space-y-0 sm:space-x-4">
                                        <button type="button" onClick={() => handleSaveCalculation(values, results)} disabled={!token || !results} className="flex items-center justify-center px-5 py-3 text-sm font-semibold text-slate-600 bg-slate-100 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 transition-colors" title={!token ? "Zaloguj się, aby zapisać" : !results ? "Najpierw wykonaj obliczenia" : "Zapisz obliczenie"}><Save size={18} className="mr-2"/> Zapisz</button>
                                        <button type="submit" disabled={isCalculating} className="flex items-center justify-center w-full sm:w-auto px-8 py-3.5 font-bold text-white bg-blue-500 rounded-2xl shadow-lg shadow-blue-500/30 disabled:bg-slate-300 disabled:shadow-none hover:bg-blue-600 transition-all">{isCalculating ? 'Liczenie...' : <>Oblicz <ChevronsRight size={20} className="ml-2"/></>}</button>
                                    </div>
                                </Form>
                            </div>
                            <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                                <div className="flex justify-between items-center mb-5">
                                    <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                                        <TrendingUp className="h-6 w-6 text-blue-500" />
                                        Wyniki Obliczeń
                                    </h2>
                                    {results && token && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch('/api/export-pdf', {
                                                        method: 'POST',
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                            'x-auth-token': btoa(token)
                                                        },
                                                        body: JSON.stringify({ calculationData: results })
                                                    });
                                                    
                                                    if (response.ok) {
                                                        const blob = await response.blob();
                                                        const url = window.URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `kalkulacja-${new Date().toISOString().split('T')[0]}.pdf`;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        window.URL.revokeObjectURL(url);
                                                        document.body.removeChild(a);
                                                        
                                                        // Jeśli to HTML (fallback), otwórz w nowej karcie
                                                        if (response.headers.get('content-type')?.includes('text/html')) {
                                                            const newWindow = window.open();
                                                            if (newWindow) {
                                                                newWindow.document.write(await blob.text());
                                                                newWindow.document.close();
                                                            }
                                                        }
                                                    } else {
                                                        showNotification({
                                                            type: 'error',
                                                            title: 'Błąd eksportu',
                                                            message: 'Nie udało się wygenerować pliku PDF. Spróbuj ponownie.'
                                                        });
                                                    }
                                                } catch (error) {
                                                    console.error('Błąd eksportu PDF:', error);
                                                    showNotification({
                                                        type: 'error',
                                                        title: 'Błąd eksportu',
                                                        message: 'Nie udało się wygenerować pliku PDF. Spróbuj ponownie.'
                                                    });
                                                }
                                            }}
                                            className="bg-red-600 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg hover:bg-red-700 flex items-center text-xs sm:text-sm"
                                        >
                                            <FileText className="mr-1 sm:mr-2" size={14} />
                                            <span className="hidden sm:inline">Eksportuj PDF</span>
                                        </button>
                                    )}
                                </div>
                                {results ? (
                                    <div className="space-y-4">
                                        <div className="p-6 sm:p-8 bg-blue-500 text-center rounded-3xl shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)]">
                                            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/80">Zysk na rękę</p>
                                            <p className="text-3xl sm:text-4xl font-extrabold text-white mt-1">{results.zyskNetto?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</p>
                                            <p className="text-sm font-medium text-white/80 mt-1">PLN miesięcznie</p>
                                        </div>
                                        {results.ostrzezenia && results.ostrzezenia.length > 0 && (
                                            <div className="p-3 bg-yellow-50 text-yellow-800 rounded-lg">
                                                <p className="font-semibold mb-2">Ostrzeżenia:</p>
                                                {results.ostrzezenia.map((warning: any, index: number) => (
                                                    <p key={index} className="text-sm">• {warning.message}</p>
                                                ))}
                                            </div>
                                        )}
                                        <ResultRow label="Przychód brutto (z VAT)" value={results.przychodBrutto} />
                                        <ResultRow label="Należny podatek VAT" value={-results.vatNalezny} />
                                        <ResultRow label="Przychód netto" value={results.przychodNetto} />
                                        <ResultRow label="Koszty" value={-results.koszty} />
                                        <ResultRow label="Dochód bieżący" value={results.dochod} isBold={true} />
                                        <ResultRow label="Składki ZUS społeczne" value={-results.zusSpoleczne} />
                                        <ResultRow label="Składka zdrowotna" value={-results.zusZdrowotne} />
                                        
                                        {/* Szczegółowy rozkład składek ZUS */}
                                        {results.zusBreakdown && (
                                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                                <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                                                    <Settings className="mr-2" size={16} />
                                                    Szczegółowy rozkład składek ZUS
                                                </h4>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Emerytalna (19,52%):</span>
                                                        <span className="font-medium">{results.zusBreakdown?.social?.emerytalna?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Rentowa (8%):</span>
                                                        <span className="font-medium">{results.zusBreakdown?.social?.rentowa?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Wypadkowa (1,67%):</span>
                                                        <span className="font-medium">{results.zusBreakdown?.social?.wypadkowa?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Chorobowa (2,45%):</span>
                                                        <span className="font-medium">{results.zusBreakdown?.social?.chorobowa?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Fundusz Pracy (2,45%):</span>
                                                        <span className="font-medium">{results.zusBreakdown?.social?.funduszPracy?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between border-t pt-2 font-semibold">
                                                        <span className="text-slate-700">RAZEM składki społeczne:</span>
                                                        <span>{results.zusBreakdown?.social?.total?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-slate-600">Składka zdrowotna:</span>
                                                        <span className="font-medium">{results.zusBreakdown?.health?.amount?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                    </div>
                                                    {results.zusBreakdown.health.minAmount && (
                                                        <div className="flex justify-between text-xs text-slate-500">
                                                            <span>Minimalna składka:</span>
                                                            <span>{results.zusBreakdown?.health?.minAmount?.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) || '0,00 zł'}</span>
                                                        </div>
                                                    )}
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        {results.zusBreakdown.health.description}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <ResultRow label="Podatek dochodowy (przed ulgami)" value={-results.podatekDochodowy} />
                                        {results.ulgiPodatkowe > 0 && (
                                            <>
                                                <ResultRow label="Ulgi podatkowe" value={results.ulgiPodatkowe} />
                                                <ResultRow label="Podatek dochodowy (po ulgach)" value={-results.podatekPoUlgach} isBold={true} />
                                            </>
                                        )}
                                        <div className="h-px bg-slate-200 my-4"></div>
                                        <Suspense fallback={<div className="text-center text-slate-500 py-10">Ładowanie wykresu...</div>}>
                                            <RevenueChart data={chartData} />
                                        </Suspense>
                                    </div>
                                ) : (
                                    <div className="text-center text-slate-400 py-16 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50">Wprowadź dane i kliknij „Oblicz”</div>
                                )}
                            </div>
                        </div>
                        
                        {/* Banner Afiliacyjny inFakt */}
                        <div className="mt-8 grid grid-cols-1">
                            <a 
                                href="https://www.infakt.pl/polecam/kalkulatorjdg" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block"
                            >
                                {/* Desktop Banner */}
                                <picture className="hidden md:block w-full max-w-4xl mx-auto">
                                    <source type="image/webp" srcSet={process.env.PUBLIC_URL + "/affiliate/infakt_pc.webp"} />
                                    <img 
                                        src={process.env.PUBLIC_URL + "/affiliate/infakt_pc.png"} 
                                        alt="inFakt - Księgowość online od 189 zł miesięcznie"
                                        className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                                        loading="lazy"
                                        decoding="async"
                                        width={800}
                                        height={200}
                                    />
                                </picture>
                                {/* Mobile Banner */}
                                <picture className="md:hidden w-full">
                                    <source type="image/webp" srcSet={process.env.PUBLIC_URL + "/affiliate/infakt_mobile.webp"} />
                                    <img 
                                        src={process.env.PUBLIC_URL + "/affiliate/infakt_mobile.png"} 
                                        alt="inFakt - Księgowość online od 189 zł miesięcznie"
                                        className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                                        loading="lazy"
                                        decoding="async"
                                        width={960}
                                        height={300}
                                    />
                                </picture>
                            </a>
                        </div>

                        {/* Banner Afiliacyjny Creative Idea */}
                        <div className="mt-8 grid grid-cols-1">
                            <a 
                                href="https://creative-idea.pl" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block"
                            >
                                {/* Desktop Banner */}
                                <picture className="hidden md:block w-full max-w-4xl mx-auto">
                                    <source type="image/webp" srcSet={process.env.PUBLIC_URL + "/affiliate/creative_idea_pc.webp"} />
                                    <img 
                                        src={process.env.PUBLIC_URL + "/affiliate/creative_idea_pc.png"} 
                                        alt="Creative Idea - Strony internetowe od 499 zł w 48H"
                                        className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                                        loading="lazy"
                                        decoding="async"
                                        width={800}
                                        height={200}
                                    />
                                </picture>
                                {/* Mobile Banner */}
                                <picture className="md:hidden w-full">
                                    <source type="image/webp" srcSet={process.env.PUBLIC_URL + "/affiliate/creative_idea_mobile.webp"} />
                                    <img 
                                        src={process.env.PUBLIC_URL + "/affiliate/creative_idea_mobile.png"} 
                                        alt="Creative Idea - Strony internetowe od 499 zł w 48H"
                                        className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                                        loading="lazy"
                                        decoding="async"
                                        width={960}
                                        height={300}
                                    />
                                </picture>
                            </a>
                        </div>
                    </>
                )}
            </Formik>
        </div>
    );
};

// --- Komponent Kalkulatora Kosztów Działalności ---
const BusinessCostCalculator = () => {
    const [costs, setCosts] = useState<any[]>([]);
    const [totalCosts, setTotalCosts] = useState(0);
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const addCost = () => {
        setCosts([...costs, { name: '', amount: 0, category: 'inne', isDeductible: true }]);
    };

    const updateCost = (index: number, field: string, value: any) => {
        const newCosts = [...costs];
        newCosts[index][field] = value;
        setCosts(newCosts);
    };

    const removeCost = (index: number) => {
        setCosts(costs.filter((_, i) => i !== index));
    };

    const calculateCosts = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/business-costs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ costs })
            });
            const data = await response.json();
            setTotalCosts(data.totalCosts || 0);
            setRecommendations(data.recommendations || []);
        } catch (error) {
            console.error('Błąd obliczania kosztów:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Twoje koszty działalności</h4>
                <button onClick={addCost} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" aria-label="Dodaj nowy koszt działalności">
                    + Dodaj koszt
                </button>
            </div>
            
            {costs.map((cost, index) => (
                <div key={index} className="flex gap-2 items-center p-3 border rounded-lg">
                    <input
                        type="text"
                        placeholder="Nazwa kosztu"
                        value={cost.name}
                        onChange={(e) => updateCost(index, 'name', e.target.value)}
                        className="flex-1 p-2 border rounded"
                    />
                    <input
                        type="number"
                        placeholder="Kwota"
                        value={cost.amount}
                        onChange={(e) => updateCost(index, 'amount', parseFloat(e.target.value) || 0)}
                        className="w-24 p-2 border rounded"
                    />
                    <select
                        value={cost.category}
                        onChange={(e) => updateCost(index, 'category', e.target.value)}
                        className="p-2 border rounded"
                    >
                        <option value="biuro">Biuro</option>
                        <option value="transport">Transport</option>
                        <option value="marketing">Marketing</option>
                        <option value="inne">Inne</option>
                    </select>
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={cost.isDeductible}
                            onChange={(e) => updateCost(index, 'isDeductible', e.target.checked)}
                            className="mr-2"
                        />
                        Odliczalny
                    </label>
                    <button onClick={() => removeCost(index)} className="text-red-600 hover:text-red-800" aria-label="Usuń ten koszt">
                        <IconX size={16} />
                    </button>
                </div>
            ))}

            {costs.length > 0 && (
                <div className="flex gap-4">
                    <button
                        onClick={calculateCosts}
                        disabled={isLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {isLoading ? 'Obliczam...' : 'Oblicz koszty'}
                    </button>
                </div>
            )}

            {totalCosts > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-semibold text-blue-800 mb-2">Podsumowanie kosztów</h5>
                    <p className="text-blue-700">Łączne koszty: {totalCosts.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}</p>
                </div>
            )}

            {recommendations.length > 0 && (
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                    <h5 className="font-semibold text-green-800 mb-2">Rekomendacje</h5>
                    <ul className="text-green-700">
                        {recommendations.map((rec, index) => (
                            <li key={index} className="mb-1">• {rec}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// --- Komponent Optymalizacji Podatkowej ---
const TaxOptimizationPanel = ({ token }: { token: string | null }) => {
    const { showNotification } = useNotification();
    const [formData, setFormData] = useState({
        income: 0,
        costs: 0,
        taxForm: 'skala',
        pkdCode: '',
        zusType: 'duzy',
        isVatPayer: false,
        vatRate: 23,
        ryczaltRate: 12
    });
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPkdModalOpen, setIsPkdModalOpen] = useState(false);
    const [selectedPkdText, setSelectedPkdText] = useState("Kliknij lupę, aby wyszukać...");
    const [businessSectors, setBusinessSectors] = useState<any[]>([]);
    const [selectedSector, setSelectedSector] = useState<any>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
    const [vatWarning, setVatWarning] = useState('');
    const [ryczaltOptions, setRyczaltOptions] = useState<any[]>([]);

    // Pobierz branże i status subskrypcji przy ładowaniu
    useEffect(() => {
        const fetchData = async () => {
            try {
                const sectorsResponse = await fetch('/api/business-sectors');
                if (sectorsResponse.ok) {
                    const sectorsData = await sectorsResponse.json();
                    setBusinessSectors(sectorsData);
                }
            } catch (error) {
                console.error('Błąd pobierania branż:', error);
            }
        };
        fetchData();

        // Pobierz status subskrypcji
        if (token) {
            fetch('/api/subscription/status', {
                headers: { 'x-auth-token': btoa(token) }
            })
            .then(res => res.json())
            .then(data => setSubscriptionStatus(data))
            .catch(err => console.error('Błąd pobierania statusu subskrypcji:', err));
        }
    }, [token]);

    // Sprawdź czy użytkownik ma dostęp (tylko EXPERT)
    // Musi być zalogowany (token) i mieć plan EXPERT lub być adminem
    // WAŻNE: Plan musi być dokładnie 'expert' (małe litery) zgodnie z backendem
    const hasAccess = token && subscriptionStatus ? (
        (subscriptionStatus.plan && subscriptionStatus.plan.toLowerCase() === 'expert') || 
        (subscriptionStatus.isAdmin === true)
    ) : false;
    
    // Dodatkowa walidacja - sprawdź czy plan NIE jest 'free' ani 'pro'
    const plan = subscriptionStatus?.plan?.toLowerCase() || 'free';
    const isNotFreeOrPro = plan !== 'free' && plan !== 'pro';

    const handleSectorChange = (sector: any) => {
        setSelectedSector(sector);
        if (sector) {
            // Automatycznie ustaw kod PKD
            if (sector.pkdCode) {
                setFormData(prev => ({...prev, pkdCode: sector.pkdCode}));
                setSelectedPkdText(`${sector.pkdCode} - ${sector.description || ''}`);
            }
            
            // Automatycznie ustaw status VAT na podstawie branży
            const isVatExempt = sector.isVatExempt;
            if (isVatExempt) {
                setFormData(prev => ({...prev, isVatPayer: false}));
            } else {
                setFormData(prev => ({...prev, isVatPayer: true, vatRate: sector.defaultVatRate || 23}));
            }
            
            // Automatycznie ustaw stawkę ryczałtu na podstawie branży
            if (sector.defaultRyczaltRate) {
                setFormData(prev => ({...prev, ryczaltRate: sector.defaultRyczaltRate, taxForm: 'ryczalt'}));
            }
        }
    };

    const handlePkdSelect = async (pkd: any) => {
        setFormData(prev => ({...prev, pkdCode: pkd.pkd_2007_code}));
        setSelectedPkdText(`${pkd.pkd_2007_code} - ${pkd.pkd_2007_description}`);
        
        // Pobierz reguły podatkowe dla wybranego PKD
        try {
            const response = await fetch(`/api/tax-rules/${pkd.pkd_2007_code}`);
            if (response.ok) {
                const data = await response.json();
                
                // Ustaw VAT
                if (data.vatRule?.is_mandatory_vat) {
                    setFormData(prev => ({...prev, isVatPayer: true, vatRate: data.vatRule.vat_rate || 23}));
                    setVatWarning(data.vatRule.ui_warning_text || '');
                } else {
                    setFormData(prev => ({...prev, isVatPayer: false}));
                    setVatWarning('');
                }
                
                // Ustaw ryczałt
                if (data.ryczaltRules && data.ryczaltRules.length > 0) {
                    setRyczaltOptions(data.ryczaltRules);
                    const allowedRyczalt = data.ryczaltRules.find((r: any) => r.allowed);
                    if (allowedRyczalt) {
                        setFormData(prev => ({
                            ...prev,
                            taxForm: 'ryczalt',
                            ryczaltRate: allowedRyczalt.rate
                        }));
                    }
                } else {
                    setRyczaltOptions([]);
                }
            }
        } catch (error) {
            console.error('Błąd pobierania stawek ryczałtu:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!token) {
            showNotification({
                type: 'warning',
                title: 'Wymagane logowanie',
                message: 'Musisz być zalogowany, aby korzystać z optymalizatora podatkowego.'
            });
            return;
        }
        
        // Sprawdź plan subskrypcji (tylko EXPERT) - PODWÓJNA WALIDACJA
        const currentPlanCheck = subscriptionStatus?.plan?.toLowerCase() || 'free';
        if (!subscriptionStatus || !hasAccess || currentPlanCheck !== 'expert') {
            const isAdminCheck = subscriptionStatus?.isAdmin === true;
            if (!isAdminCheck) {
                showNotification({
                    type: 'warning',
                    title: 'Wymagany plan EXPERT',
                    message: 'Optymalizacja podatkowa wymaga planu EXPERT. Upgrade w Panelu > Subskrypcja',
                    showGoToSubscription: true,
                    onGoToSubscription: () => {
                        localStorage.setItem('showSubscription', 'true');
                        window.location.href = '/#dashboard';
                    }
                });
                return;
            }
        }
        
        // Walidacja przychodu - nie może być 0 lub mniejszy
        if (!formData.income || formData.income <= 0) {
            showNotification({
                type: 'warning',
                title: 'Brak przychodu',
                message: 'Proszę uzupełnić roczny przychód (wartość musi być większa od 0), aby wygenerować rekomendacje optymalizacji podatkowej.'
            });
            return;
        }
        
        if (!formData.pkdCode) {
            showNotification({
                type: 'warning',
                title: 'Brakujący kod PKD',
                message: 'Proszę wybrać kod PKD lub branżę działalności.'
            });
            return;
        }
        
        setIsLoading(true);
        setRecommendations([]);
        
        try {
            const headers: Record<string, string> = { 
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['x-auth-token'] = btoa(token);
            }
            
            const response = await fetch('/api/tax-optimization', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(formData)
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Musisz być zalogowany');
                } else if (response.status === 403) {
                    throw new Error('Wymagany plan EXPERT. Upgrade w Panelu > Subskrypcja');
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Błąd serwera');
            }
            
            const data = await response.json();
            setRecommendations(data.recommendations || []);
            
            if (!data.recommendations || data.recommendations.length === 0) {
                showNotification({
                    type: 'info',
                    title: 'Brak rekomendacji',
                    message: 'Nie znaleziono rekomendacji optymalizacji dla podanych danych.'
                });
            }
        } catch (error: any) {
            console.error('Błąd optymalizacji:', error);
            showNotification({
                type: 'error',
                title: 'Błąd optymalizacji',
                message: error.message || 'Wystąpił błąd podczas generowania rekomendacji.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Jeśli status nie jest jeszcze załadowany, pokaż loading
    if (token && !subscriptionStatus) {
        return (
            <div className="p-6 text-center">
                <p className="text-slate-600">Ładowanie danych subskrypcji...</p>
            </div>
        );
    }

    // Jeśli użytkownik nie jest zalogowany
    if (!token) {
        return (
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">Wymagane logowanie</h4>
                <p className="text-blue-700 mb-4">
                    Musisz być zalogowany, aby korzystać z optymalizatora podatkowego.
                </p>
            </div>
        );
    }

    // Jeśli brak dostępu (FREE, PRO lub niezaładowany status), wyświetl komunikat
    // Sprawdź BEZWZGLĘDNIE czy plan to EXPERT (sprawdzenie wielkości liter)
    const currentPlan = subscriptionStatus?.plan?.toLowerCase() || 'free';
    const hasExpertAccess = currentPlan === 'expert' || subscriptionStatus?.isAdmin === true;
    
    if (!token || !subscriptionStatus || !hasExpertAccess || !hasAccess || !isNotFreeOrPro) {
        return (
            <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">Wymagany plan EXPERT</h4>
                <p className="text-yellow-700 mb-4">
                    Optymalizacja podatkowa jest dostępna wyłącznie dla użytkowników z planem <span className="font-bold">EXPERT</span>.
                    {subscriptionStatus && subscriptionStatus.plan && (
                        <span className="block mt-2">Twój obecny plan: <span className="font-semibold uppercase">{subscriptionStatus.plan}</span></span>
                    )}
                </p>
                <button
                    onClick={() => {
                        localStorage.setItem('showSubscription', 'true');
                        window.location.href = '/#dashboard';
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                    Przejdź do Subskrypcji
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Przychód roczny (PLN) <span className="text-red-500">*</span></label>
                    <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={formData.income || ''}
                        onChange={(e) => setFormData({...formData, income: parseFloat(e.target.value) || 0})}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                        required
                        placeholder="Wprowadź przychód większy od 0"
                    />
                    {formData.income === 0 && (
                        <p className="mt-1 text-xs text-red-600">Przychód musi być większy od 0</p>
                    )}
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Koszty roczne (PLN)</label>
                    <input
                        type="number"
                        value={formData.costs}
                        onChange={(e) => setFormData({...formData, costs: parseFloat(e.target.value) || 0})}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                        required
                    />
                </div>
                
                {/* Wybór branży działalności */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Branża działalności (opcjonalnie)</label>
                    <select 
                        value={selectedSector?._id || ''} 
                        onChange={(e) => {
                            const sector = businessSectors.find(s => s._id === e.target.value);
                            handleSectorChange(sector || null);
                        }}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                    >
                        <option value="">Wybierz branżę...</option>
                        {businessSectors.map(sector => (
                            <option key={sector._id} value={sector._id}>
                                {sector.name} - {sector.description}
                            </option>
                        ))}
                    </select>
                    {selectedSector && (
                        <div className="mt-2 p-4 bg-blue-50 rounded-2xl text-sm border border-blue-100">
                            <p><strong>Wybrana branża:</strong> {selectedSector.name}</p>
                            <p><strong>Kod PKD:</strong> {selectedSector.pkdCode}</p>
                            <p><strong>VAT:</strong> {selectedSector.isVatExempt ? 'Zwolniony' : `Czynny (${selectedSector.defaultVatRate}%)`}</p>
                            {selectedSector.defaultRyczaltRate && (
                                <p><strong>Ryczałt:</strong> {selectedSector.defaultRyczaltRate}%</p>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Wybór kodu PKD */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod PKD (opcjonalnie)</label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={selectedPkdText}
                            readOnly
                            className="flex-1 p-3.5 bg-slate-50 border-2 border-slate-100 rounded-l-2xl text-slate-800"
                            placeholder="Kliknij lupę, aby wyszukać..."
                        />
                        <button
                            type="button"
                            onClick={() => setIsPkdModalOpen(true)}
                            className="p-3.5 bg-blue-500 text-white rounded-r-2xl hover:bg-blue-600 transition"
                            aria-label="Wyszukaj kod PKD"
                        >
                            <Search size={20} />
                        </button>
                    </div>
                </div>
                
                {/* Status VAT */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status VAT</label>
                    <select
                        value={formData.isVatPayer ? 'true' : 'false'}
                        onChange={(e) => setFormData({...formData, isVatPayer: e.target.value === 'true'})}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                    >
                        <option value="false">Zwolniony</option>
                        <option value="true">Czynny</option>
                    </select>
                    {selectedSector && (
                        <div className="mt-1 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                            <strong>Sugerowane:</strong> {selectedSector.isVatExempt ? 'Zwolniony' : 'Czynny'}
                        </div>
                    )}
                    {vatWarning && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                            {vatWarning}
                        </div>
                    )}
                </div>
                
                {/* Forma opodatkowania */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Forma opodatkowania</label>
                    <select
                        value={formData.taxForm}
                        onChange={(e) => setFormData({...formData, taxForm: e.target.value})}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                    >
                        <option value="skala">Skala Podatkowa</option>
                        <option value="ryczalt">Ryczałt Ewidencjonowany</option>
                        <option value="liniowy">Podatek Liniowy 19%</option>
                    </select>
                    {formData.taxForm === 'ryczalt' && ryczaltOptions.length > 0 && (
                        <div className="mt-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Stawka ryczałtu</label>
                            <select
                                value={formData.ryczaltRate}
                                onChange={(e) => setFormData({...formData, ryczaltRate: parseFloat(e.target.value)})}
                                className="w-full p-3 bg-white border-2 border-slate-100 rounded-2xl text-sm text-slate-900 focus:border-blue-500 focus:ring-0"
                            >
                                {ryczaltOptions.filter((r: any) => r.allowed).map((rule: any) => (
                                    <option key={rule.rate} value={rule.rate}>
                                        {rule.rate}%
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Typ ZUS</label>
                    <select
                        value={formData.zusType}
                        onChange={(e) => setFormData({...formData, zusType: e.target.value})}
                        className="w-full p-3.5 bg-white border-2 border-slate-100 rounded-2xl text-slate-900 focus:border-blue-500 focus:ring-0"
                    >
                        <option value="duzy">Duży ZUS</option>
                        <option value="maly">Mały ZUS (preferencyjny - nowe firmy)</option>
                    </select>
                </div>
                
                {isPkdModalOpen && (
                    <PKDModal
                        onSelect={handlePkdSelect}
                        onClose={() => setIsPkdModalOpen(false)}
                    />
                )}
            </div>
            
            <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 disabled:bg-slate-300 disabled:shadow-none transition-all shadow-lg shadow-blue-500/20"
            >
                {isLoading ? 'Analizuję...' : 'Generuj rekomendacje optymalizacji'}
            </button>

            {recommendations.length > 0 && (
                <div className="mt-6 p-5 bg-yellow-50 rounded-2xl border border-yellow-100">
                    <h5 className="font-bold text-yellow-800 mb-3">Rekomendacje optymalizacji podatkowej</h5>
                    <div className="space-y-2">
                        {recommendations.map((rec, index) => (
                            <div key={index} className="p-3 bg-white rounded border-l-4 border-yellow-400">
                                <div className="text-yellow-700">
                                    {typeof rec === 'string' ? (
                                        <p>{rec}</p>
                                    ) : (
                                        <div>
                                            <h6 className="font-semibold text-yellow-800">{rec.title}</h6>
                                            <p className="text-sm">{rec.message}</p>
                                            {rec.potentialSavings && (
                                                <p className="text-xs text-green-600 mt-1">
                                                    Potencjalna oszczędność: {rec.potentialSavings} PLN
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </form>
    );
};

// --- Komponent Panelu Użytkownika ---
const Dashboard = ({ token, onUpgrade, onMetaUpdate }: { token: string, onUpgrade: (plan: 'pro' | 'expert') => void, onMetaUpdate: (title: string, description: string, keywords?: string) => void }) => {
    const { showNotification } = useNotification();
    const [view, setView] = useState<'calculations' | 'chats' | 'admin' | 'business-costs' | 'tax-optimization' | 'subscription'>('calculations');
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);

    // Aktualizacja meta tagów dla widoków dashboard
    useEffect(() => {
        if (!token) return;
        
        const updateDashboardMeta = () => {
            switch(view) {
                case 'tax-optimization':
                    onMetaUpdate(
                        'Optymalizator Podatkowy AI - Kalkulator JDG 2026 | Plan EXPERT',
                        'Zaawansowany optymalizator podatkowy z AI dla działalności gospodarczej. Plan EXPERT. Automatyczne rekomendacje optymalizacji podatkowej.',
                        'optymalizator podatkowy, optymalizacja podatkowa jdg, rekomendacje podatkowe, plan expert'
                    );
                    break;
                case 'subscription':
                    onMetaUpdate(
                        'Plany Subskrypcji - Kalkulator JDG 2026 | FREE, PRO, EXPERT',
                        'Wybierz plan subskrypcji KalkulatorJDG.pl. Porównaj plany FREE, PRO i EXPERT. Funkcje premium z AI wsparciem.',
                        'subskrypcja kalkulator jdg, plany subskrypcji, kalkulator premium, plan pro, plan expert'
                    );
                    break;
                case 'chats':
                    onMetaUpdate(
                        'Historia Czatów z AI - Kalkulator JDG 2026',
                        'Przeglądaj historię rozmów z asystentem AI podatkowym. Wszystkie zapisane konwersacje w jednym miejscu.',
                        'historia czatów ai, asystent ai podatkowy, czat kalkulator jdg'
                    );
                    break;
                case 'calculations':
                    onMetaUpdate(
                        'Zapisane Obliczenia - Kalkulator JDG 2026',
                        'Twoje zapisane obliczenia podatkowe. Powróć do wcześniejszych kalkulacji i porównaj wyniki.',
                        'zapisane obliczenia, historia kalkulacji, zapisane kalkulacje jdg'
                    );
                    break;
                default:
                    break;
            }
        };
        
        updateDashboardMeta();
    }, [view, token, onMetaUpdate]);
    const [calculations, setCalculations] = useState([]);
    const [chats, setChats] = useState([]);
    const [updateMessage, setUpdateMessage] = useState('');
    const [selectedCalculation, setSelectedCalculation] = useState<any>(null);
    const [chatHistory, setChatHistory] = useState<any[]>([]);

    const handleUpdateConstants = async () => {
        try {
            const response = await fetch('/api/update-constants', {
                headers: { 'x-auth-token': btoa(token) }
            });
            const data = await response.json();
            setUpdateMessage(data.message || 'Stałe zostały zaktualizowane');
            setTimeout(() => setUpdateMessage(''), 3000);
        } catch (error) {
            setUpdateMessage('Błąd aktualizacji stałych');
            setTimeout(() => setUpdateMessage(''), 3000);
        }
    };

    const handleUpdateBiznesData = async () => {
        try {
            setUpdateMessage('Aktualizuję dane z biznes.gov.pl...');
            const response = await fetch('/api/update-biznes-data', {
                method: 'POST',
                headers: { 'x-auth-token': btoa(token) }
            });
            const data = await response.json();
            if (data.success) {
                setUpdateMessage('✅ Dane zaktualizowane pomyślnie z biznes.gov.pl');
            } else {
                setUpdateMessage('❌ Błąd aktualizacji danych: ' + data.error);
            }
            setTimeout(() => setUpdateMessage(''), 5000);
        } catch (error) {
            setUpdateMessage('❌ Błąd połączenia z serwerem');
            setTimeout(() => setUpdateMessage(''), 3000);
        }
    };

    const handleViewCalculation = async (calculationId: string) => {
        try {
            const response = await fetch(`/api/calculations/${calculationId}`, {
                headers: { 'x-auth-token': btoa(token) }
            });
            if (response.ok) {
                const calculation = await response.json();
                setSelectedCalculation(calculation);
            }
        } catch (error) {
            console.error('Błąd pobierania obliczenia:', error);
        }
    };

    const handleDeleteCalculation = async (calculationId: string) => {
        try {
            const response = await fetch(`/api/calculations/${calculationId}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': btoa(token) }
            });
            if (response.ok) {
                // Odśwież listę obliczeń
                const calculationsResponse = await fetch('/api/calculations', {
                    headers: { 'x-auth-token': btoa(token) }
                });
                if (calculationsResponse.ok) {
                    const calculationsData = await calculationsResponse.json();
                    setCalculations(calculationsData);
                }
            }
        } catch (error) {
            console.error('Błąd usuwania obliczenia:', error);
        }
    };

    const handleViewChatHistory = async () => {
        try {
            const response = await fetch('/api/chat-history', {
                headers: { 'x-auth-token': btoa(token) }
            });
            if (response.ok) {
                const history = await response.json();
                setChatHistory(history);
            }
        } catch (error) {
            console.error('Błąd pobierania historii czatu:', error);
        }
    };

    const handleDeleteChatHistory = async () => {
        try {
            const response = await fetch('/api/chat-history', {
                method: 'DELETE',
                headers: { 'x-auth-token': btoa(token) }
            });
            if (response.ok) {
                setChatHistory([]);
            }
        } catch (error) {
            console.error('Błąd usuwania historii czatu:', error);
        }
    };

    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            try {
                const response = await fetch('/api/subscription/status', {
                    headers: { 'x-auth-token': btoa(token) }
                });
                if (response.ok) {
                    const data = await response.json();
                    setSubscriptionStatus(data);
                } else if (response.status === 401) {
                    const errorData = await response.json().catch(() => ({}));
                    if (errorData.expired) {
                        // Token wygasł - wyczyść i wyślij event
                        localStorage.removeItem('token');
                        window.dispatchEvent(new CustomEvent('token-expired'));
                    }
                }
            } catch (error) {
                console.error('Błąd pobierania statusu subskrypcji:', error);
            }
        };
        
        if (token) {
            fetchSubscriptionStatus();
        }
        
        // Listener dla eventu show-subscription - obsługuj zarówno z localStorage jak i z eventu
        const handleShowSubscription = () => {
            setView('subscription');
            // Upewnij się że localStorage jest czysty
            if (localStorage.getItem('showSubscription') === 'true') {
                localStorage.removeItem('showSubscription');
            }
        };
        window.addEventListener('show-subscription', handleShowSubscription);
        
        // Sprawdź localStorage dla showSubscription przy montowaniu - wywołaj handler
        if (localStorage.getItem('showSubscription') === 'true') {
            // Małe opóźnienie aby upewnić się że komponent jest zamontowany
            setTimeout(() => {
                handleShowSubscription();
            }, 100);
        }
        
        return () => {
            window.removeEventListener('show-subscription', handleShowSubscription);
        };
    }, [token]);
    
    useEffect(() => {
        const fetchData = async () => {
            const headers: Record<string, string> = {};
            if (token && typeof token === 'string') {
                headers['x-auth-token'] = btoa(token);
            }
            if (view === 'calculations') {
                const res = await fetch('/api/calculations', { headers });
                const data = await res.json();
                setCalculations(data);
            } else {
                const res = await fetch('/api/chatbot/history', { headers });
                const data = await res.json();
                setChats(data);
            }
        };
        fetchData();
    }, [view, token]);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Panel Użytkownika</h2>
            
            {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
            <div className="mb-6">
                {/* Mobile: Vertical layout */}
                <div className="grid grid-cols-2 sm:hidden gap-2">
                    <button onClick={() => setView('calculations')} className={`p-3 rounded-2xl flex flex-col items-center justify-center ${view === 'calculations' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <Calculator size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Zapisane Obliczenia</span>
                    </button>
                    <button onClick={() => setView('chats')} className={`p-3 rounded-2xl flex flex-col items-center justify-center ${view === 'chats' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <MessageSquare size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Historia Czatów</span>
                    </button>
                    <button onClick={() => setView('business-costs')} className={`p-3 rounded-2xl flex flex-col items-center justify-center ${view === 'business-costs' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <TrendingUp size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Koszty Działalności</span>
                    </button>
                    <button onClick={() => setView('tax-optimization')} className={`p-3 rounded-2xl flex flex-col items-center justify-center relative ${view === 'tax-optimization' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <AlertTriangle size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Optymalizacja Podatkowa</span>
                        <span className="absolute top-1 right-1 bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded text-xs font-bold">EXPERT</span>
                    </button>
                    <button onClick={() => setView('subscription')} className={`p-3 rounded-2xl flex flex-col items-center justify-center ${view === 'subscription' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <CreditCard size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Subskrypcja</span>
                    </button>
                    <button onClick={() => setView('admin')} className={`p-3 rounded-2xl flex flex-col items-center justify-center ${view === 'admin' ? 'bg-blue-50 text-blue-600 shadow-sm border border-blue-100' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'}`}>
                        <Settings size={24} className="mb-1"/>
                        <span className="text-xs text-center leading-tight">Administracja</span>
                    </button>
                </div>
                
                {/* Desktop: Horizontal layout */}
                <div className="hidden sm:flex border-b border-slate-200">
                    <button onClick={() => setView('calculations')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm ${view === 'calculations' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><Calculator size={18} className="mr-2"/> Zapisane Obliczenia</button>
                    <button onClick={() => setView('chats')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm ${view === 'chats' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><MessageSquare size={18} className="mr-2"/> Historia Czatów</button>
                    <button onClick={() => setView('business-costs')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm ${view === 'business-costs' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><TrendingUp size={18} className="mr-2"/> Koszty Działalności</button>
                    <button onClick={() => setView('tax-optimization')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm gap-2 ${view === 'tax-optimization' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}>
                        <AlertTriangle size={18} />
                        <span>Optymalizacja</span>
                        <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-xs font-bold">EXPERT</span>
                    </button>
                    <button onClick={() => setView('subscription')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm ${view === 'subscription' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><CreditCard size={18} className="mr-2"/> Subskrypcja</button>
                    <button onClick={() => setView('admin')} className={`px-4 py-2 flex items-center whitespace-nowrap text-sm ${view === 'admin' ? 'border-b-2 border-blue-500 font-semibold text-blue-600' : 'text-slate-600 hover:text-slate-900'}`}><Settings size={18} className="mr-2"/> Administracja</button>
                </div>
            </div>
            {view === 'subscription' ? (
                <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                    <div className="space-y-6">
                        <SubscriptionStatus token={token} />
                        <SubscriptionPlans 
                            token={token} 
                            currentPlan={subscriptionStatus?.plan || 'free'}
                            onUpgrade={onUpgrade}
                            isAdmin={subscriptionStatus?.isAdmin || false}
                            onSwitchPlan={async (plan: 'free' | 'pro' | 'expert') => {
                                if (!token) return;
                                try {
                                    const response = await fetch('/api/subscription/admin-switch', {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'x-auth-token': btoa(token)
                                        },
                                        body: JSON.stringify({ plan })
                                    });
                                    const data = await response.json();
                                    if (!response.ok) throw new Error(data.error || 'Błąd zmiany planu');
                                    
                                    // Odśwież status subskrypcji
                                    const statusResponse = await fetch('/api/subscription/status', {
                                        headers: { 'x-auth-token': btoa(token) }
                                    });
                                    const statusData = await statusResponse.json();
                                    setSubscriptionStatus(statusData);
                                    
                                    showNotification({
                                        type: 'success',
                                        title: 'Sukces',
                                        message: `Plan zmieniony na ${plan.toUpperCase()}`
                                    });
                                } catch (error: any) {
                                    showNotification({
                                        type: 'error',
                                        title: 'Błąd',
                                        message: error.message || 'Nie udało się zmienić planu'
                                    });
                                }
                            }}
                        />
                    </div>
                </Suspense>
            ) : view === 'calculations' ? (
                <div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Twoje zapisane kalkulacje</h3>
                    {calculations.length > 0 ? calculations.map((calc: any) => (
                        <div key={calc._id} className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-100 shadow-sm mb-4">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm sm:text-base truncate text-slate-900">Kalkulacja z dnia: {new Date(calc.createdAt).toLocaleString()}</p>
                                    <p className="text-xs sm:text-sm text-slate-600 break-words">Przychód: {calc.inputs?.income?.toLocaleString()} PLN | Koszty: {calc.inputs?.costs?.toLocaleString()} PLN</p>
                                </div>
                                <div className="flex space-x-2 sm:justify-end">
                                    <button 
                                        onClick={() => handleViewCalculation(calc._id)}
                                        className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-blue-600 transition-colors"
                                    >
                                        Podgląd
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteCalculation(calc._id)}
                                        className="flex-1 sm:flex-none px-4 py-2 bg-red-500 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-red-600 transition-colors"
                                    >
                                        Usuń
                                    </button>
                                </div>
                            </div>
                        </div>
                    )) : <p className="text-sm sm:text-base text-slate-600">Brak zapisanych obliczeń.</p>}
                </div>
            ) : view === 'chats' ? (
                <div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3 sm:gap-0">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">Twoja historia rozmów z AI</h3>
                        <div className="flex space-x-2">
                            <button 
                                onClick={handleViewChatHistory}
                                className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-blue-600 transition-colors"
                            >
                                <span className="hidden sm:inline">Odśwież historię</span><span className="sm:hidden">Odśwież</span>
                            </button>
                            <button 
                                onClick={handleDeleteChatHistory}
                                className="flex-1 sm:flex-none px-4 py-2 bg-red-500 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-red-600 transition-colors"
                            >
                                <span className="hidden sm:inline">Wyczyść historię</span><span className="sm:hidden">Wyczyść</span>
                            </button>
                        </div>
                    </div>
                    {chatHistory.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {chatHistory.map((chat: any, index: number) => (
                                <div key={index} className={`p-3 sm:p-4 rounded-2xl ${chat.sender === 'user' ? 'bg-blue-50 ml-4 sm:ml-8 border border-blue-100' : 'bg-slate-50 mr-4 sm:mr-8 border border-slate-100'}`}>
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-0">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs sm:text-sm font-semibold mb-1 text-slate-900">
                                                {chat.sender === 'user' ? 'Ty' : 'Asystent AI'}
                                            </p>
                                            <p className="text-xs sm:text-sm break-words text-slate-700">{chat.text}</p>
                                        </div>
                                        <span className="text-xs text-slate-500 sm:ml-2 flex-shrink-0">
                                            {new Date(chat.timestamp).toLocaleTimeString()}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-slate-600">Brak historii czatów. Rozpocznij rozmowę z asystentem AI!</p>
                    )}
                </div>
            ) : view === 'business-costs' ? (
                <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Kalkulator Kosztów Działalności</h3>
                    <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                        <p className="text-slate-600 mb-4">Oblicz koszty działalności i otrzymaj rekomendacje optymalizacyjne.</p>
                        <BusinessCostCalculator />
                    </div>
                </div>
            ) : view === 'tax-optimization' ? (
                <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Optymalizacja Podatkowa</h3>
                    <div className="bg-white p-6 sm:p-8 rounded-[28px] border border-slate-100 shadow-xl shadow-slate-200/50">
                        <p className="text-slate-600 mb-4">Otrzymaj rekomendacje optymalizacji podatkowej dla Twojej działalności.</p>
                        <TaxOptimizationPanel token={token} />
                    </div>
                </div>
            ) : (
                <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4">Panel Administracyjny</h3>
                    <div className="space-y-4">
                        <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-sm">
                            <h4 className="font-bold text-slate-900 mb-2">Aktualizacja Stałych Finansowych</h4>
                            <p className="text-slate-600 mb-4">Zaktualizuj stałe finansowe na aktualny rok podatkowy.</p>
                            <div className="space-y-2">
                                <button onClick={handleUpdateConstants} className="px-5 py-2.5 bg-blue-500 text-white rounded-2xl font-semibold hover:bg-blue-600 mr-2 transition-colors">
                                    Aktualizuj Stałe
                                </button>
                                <button onClick={handleUpdateBiznesData} className="px-5 py-2.5 bg-green-600 text-white rounded-2xl font-semibold hover:bg-green-700 transition-colors">
                                    🔄 Aktualizuj z biznes.gov.pl
                                </button>
                            </div>
                            {updateMessage && (
                                <p className="mt-2 text-green-600">{updateMessage}</p>
                            )}
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <h4 className="font-semibold mb-2">Informacje o Systemie</h4>
                            <div className="text-sm text-gray-600">
                                <p>• Wersja aplikacji: 2.0.0</p>
                                <p>• Rok podatkowy: 2025</p>
                                <p>• Ostatnia aktualizacja stałych: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modal podglądu obliczenia */}
            {selectedCalculation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-4xl w-full mx-4 max-h-96 overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">Szczegóły obliczenia</h3>
                            <button 
                                onClick={() => setSelectedCalculation(null)}
                                className="text-gray-500 hover:text-gray-700"
                                aria-label="Zamknij szczegóły obliczenia"
                            >
                                <IconX size={24} />
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-semibold mb-2">Dane wejściowe</h4>
                                <div className="space-y-2 text-sm">
                                    <p><strong>Przychód:</strong> {selectedCalculation.inputs?.income?.toLocaleString()} PLN</p>
                                    <p><strong>Koszty:</strong> {selectedCalculation.inputs?.costs?.toLocaleString()} PLN</p>
                                    <p><strong>Forma opodatkowania:</strong> {selectedCalculation.inputs?.taxForm}</p>
                                    <p><strong>VAT:</strong> {selectedCalculation.inputs?.isVatPayer ? 'Czynny' : 'Zwolniony'}</p>
                                    <p><strong>Stawka VAT:</strong> {selectedCalculation.inputs?.vatRate}%</p>
                                    <p><strong>ZUS:</strong> {selectedCalculation.inputs?.zusType}</p>
                                    <p><strong>Data:</strong> {new Date(selectedCalculation.createdAt).toLocaleString()}</p>
                                </div>
                            </div>
                            
                            <div>
                                <h4 className="font-semibold mb-2">Wyniki</h4>
                                <div className="space-y-2 text-sm">
                                    <p><strong>Przychód netto:</strong> {selectedCalculation.outputs?.przychodNetto?.toLocaleString()} PLN</p>
                                    <p><strong>Podatek dochodowy:</strong> {selectedCalculation.outputs?.podatekDochodowy?.toLocaleString()} PLN</p>
                                    <p><strong>Składki ZUS:</strong> {(selectedCalculation.outputs?.zusSpoleczne + selectedCalculation.outputs?.zusZdrowotne)?.toLocaleString()} PLN</p>
                                    <p><strong>Składka zdrowotna:</strong> {selectedCalculation.outputs?.zusZdrowotne?.toLocaleString()} PLN</p>
                                    <p><strong>Zysk netto:</strong> {selectedCalculation.outputs?.zyskNetto?.toLocaleString()} PLN</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


// --- Komponent Autoryzacji w Modalu ---
const AuthModal = ({ setToken, onClose }: { setToken: (token: string) => void, onClose: () => void }) => {
    const [authMode, setAuthMode] = useState<'login' | 'register' | 'verify' | 'forgot-password' | 'verify-reset-code' | 'reset-password'>('login');
    const [emailForVerification, setEmailForVerification] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleRegister = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            
            // Obsługa rate limitingu
            if (response.status === 429) {
                const data = await response.json().catch(() => ({ error: 'Zbyt wiele prób. Spróbuj ponownie za chwilę.' }));
                setError(data.error || 'Zbyt wiele prób rejestracji. Spróbuj ponownie za 15 minut.');
                return;
            }
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.msg || 'Błąd rejestracji');
            setSuccess(data.msg);
            setEmailForVerification(values.email);
            setAuthMode('verify');
        } catch (err: any) {
            setError(err.message || 'Wystąpił błąd podczas rejestracji');
        }
    };

    const handleVerify = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: emailForVerification, activationCode: values.activationCode })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.msg || 'Błąd weryfikacji');
            setSuccess(data.msg);
            setAuthMode('login');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleLogin = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            
            // Obsługa rate limitingu
            if (response.status === 429) {
                const data = await response.json().catch(() => ({ error: 'Zbyt wiele prób. Spróbuj ponownie za chwilę.' }));
                setError(data.error || 'Zbyt wiele prób logowania. Spróbuj ponownie za 15 minut.');
                return;
            }
            
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.msg || 'Błąd logowania');
            if (data.token) {
                localStorage.setItem('token', data.token);
                setToken(data.token);
                onClose();
            }
        } catch (err: any) {
            setError(err.message || 'Wystąpił błąd podczas logowania');
        }
    };

    const handleForgotPassword = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: values.email })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.msg || 'Błąd żądania resetu hasła');
            
            setSuccess(data.msg);
            setEmailForVerification(values.email);
            setAuthMode('verify-reset-code');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleVerifyResetCode = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/verify-reset-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    email: emailForVerification, 
                    code: values.code 
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.msg || 'Błąd weryfikacji kodu');
            
            setSuccess(data.msg);
            setResetToken(data.resetToken);
            setAuthMode('reset-password');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleResetPassword = async (values: any) => {
        setError(''); setSuccess('');
        try {
            const response = await fetch(`/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    token: resetToken,
                    newPassword: values.newPassword 
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.msg || 'Błąd resetu hasła');
            
            setSuccess(data.msg);
            setTimeout(() => {
                setAuthMode('login');
                setResetToken('');
            }, 2000);
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4">
            <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-slate-100 relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-800 transition-colors" aria-label="Zamknij okno"><IconX size={24} /></button>
                {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                {success && <p className="text-green-500 text-center mb-4">{success}</p>}

                {authMode === 'verify' ? (
                    <>
                        <h2 className="text-2xl font-bold text-center mb-6">Weryfikacja Konta</h2>
                        <p className="text-center text-sm mb-4">Wysłaliśmy 6-cyfrowy kod na adres {emailForVerification}. Sprawdź swoją skrzynkę (i folder spam).</p>
                        <Formik initialValues={{ activationCode: '' }} onSubmit={handleVerify} enableReinitialize>
                            <Form className="space-y-6">
                                <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod Weryfikacyjny</label><Field name="activationCode" type="text" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                <button type="submit" className="w-full p-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition-colors">Weryfikuj</button>
                            </Form>
                        </Formik>
                    </>
                ) : authMode === 'forgot-password' ? (
                    <>
                        <h2 className="text-2xl font-bold text-center mb-6">Reset Hasła</h2>
                        <p className="text-center text-sm mb-4">Wprowadź swój adres e-mail, aby otrzymać kod weryfikacyjny.</p>
                        <Formik initialValues={{ email: '' }} onSubmit={handleForgotPassword} enableReinitialize>
                            <Form className="space-y-6">
                                <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Email</label><Field name="email" type="email" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                <button type="submit" className="w-full p-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition-colors">Wyślij kod weryfikacyjny</button>
                                <p className="text-sm text-center">
                                    <button type="button" onClick={() => setAuthMode('login')} className="font-semibold text-blue-600 hover:text-blue-700">Powrót do logowania</button>
                                </p>
                            </Form>
                        </Formik>
                    </>
                ) : authMode === 'verify-reset-code' ? (
                    <>
                        <h2 className="text-2xl font-bold text-center mb-6">Weryfikacja Kodu</h2>
                        <p className="text-center text-sm mb-4">Wysłaliśmy 6-cyfrowy kod na adres {emailForVerification}. Sprawdź swoją skrzynkę (i folder spam).</p>
                        <Formik initialValues={{ code: '' }} onSubmit={handleVerifyResetCode} enableReinitialize>
                            <Form className="space-y-6">
                                <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kod Weryfikacyjny</label><Field name="code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl text-center text-2xl tracking-widest font-mono focus:border-blue-500 focus:ring-0" /></div>
                                <button type="submit" className="w-full p-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition-colors">Weryfikuj Kod</button>
                                <p className="text-sm text-center">
                                    <button type="button" onClick={() => setAuthMode('forgot-password')} className="font-semibold text-blue-600 hover:text-blue-700">Wyślij nowy kod</button>
                                </p>
                                <p className="text-sm text-center">
                                    <button type="button" onClick={() => setAuthMode('login')} className="font-semibold text-blue-600 hover:text-blue-700">Powrót do logowania</button>
                                </p>
                            </Form>
                        </Formik>
                    </>
                ) : authMode === 'reset-password' ? (
                    <>
                        <h2 className="text-2xl font-bold text-center mb-6">Ustaw Nowe Hasło</h2>
                        <p className="text-center text-sm mb-4">Wprowadź nowe hasło dla swojego konta.</p>
                        <Formik initialValues={{ newPassword: '', confirmPassword: '' }} onSubmit={handleResetPassword} enableReinitialize>
                            {({ values }) => (
                                <Form className="space-y-6">
                                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Nowe Hasło</label><Field name="newPassword" type="password" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                    <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Potwierdź Hasło</label><Field name="confirmPassword" type="password" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                    {values.newPassword && values.confirmPassword && values.newPassword !== values.confirmPassword && (
                                        <p className="text-red-500 text-sm">Hasła nie są identyczne</p>
                                    )}
                                    <button 
                                        type="submit" 
                                        disabled={!values.newPassword || !values.confirmPassword || values.newPassword !== values.confirmPassword}
                                        className="w-full p-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 disabled:bg-slate-300 disabled:hover:bg-slate-300 transition-colors"
                                    >
                                        Zresetuj Hasło
                                    </button>
                                    <p className="text-sm text-center">
                                        <button type="button" onClick={() => setAuthMode('login')} className="font-semibold text-blue-600 hover:text-blue-700">Powrót do logowania</button>
                                    </p>
                                </Form>
                            )}
                        </Formik>
                    </>
                ) : (
                    <>
                        <h2 className="text-2xl font-bold text-center mb-6">{authMode === 'login' ? 'Logowanie' : 'Rejestracja'}</h2>
                        <Formik initialValues={{ email: '', password: '' }} onSubmit={authMode === 'login' ? handleLogin : handleRegister} enableReinitialize>
                            <Form className="space-y-6">
                                <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Email</label><Field name="email" type="email" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                <div><label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Hasło</label><Field name="password" type="password" required className="w-full p-3.5 mt-1 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-0" /></div>
                                <button type="submit" className="w-full p-3.5 bg-blue-500 text-white rounded-2xl font-bold hover:bg-blue-600 transition-colors">{authMode === 'login' ? 'Zaloguj się' : 'Zarejestruj się'}</button>
                                <div className="text-sm text-center space-y-2">
                                    <p>{authMode === 'login' ? "Nie masz konta?" : "Masz już konto?"}<button type="button" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} className="font-semibold text-blue-600 hover:text-blue-700 ml-1">{authMode === 'login' ? 'Zarejestruj się' : 'Zaloguj się'}</button></p>
                                    {authMode === 'login' && (
                                        <p><button type="button" onClick={() => setAuthMode('forgot-password')} className="font-semibold text-blue-600 hover:text-blue-700">Zapomniałeś hasła?</button></p>
                                    )}
                                </div>
                            </Form>
                        </Formik>
                    </>
                )}
            </div>
        </div>
    );
};

// --- Strona główna z redesignem (hero + sekcje marketingowe) ---

const RedesignedCalculatorPage = ({
    token,
    onUpgrade,
}: {
    token: string | null;
    onUpgrade: (plan: 'pro' | 'expert') => void;
}) => (
    <div className="bg-gray-50">
        {/* HERO */}
        <section className="relative overflow-hidden pt-16 pb-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
            <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/20 blur-3xl" />
            <div className="relative z-10 mx-auto flex max-w-7xl flex-col gap-16 px-4 sm:px-6 lg:grid lg:grid-cols-2 lg:items-center lg:px-8">
                {/* Tekst po lewej */}
                <div>
                    <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5">
                        <span className="flex h-2 w-2 rounded-full bg-blue-400" />
                        <span className="text-sm font-medium text-white/80">
                            Zgodne z przepisami 2026
                        </span>
                    </div>
                    <h1 className="mb-4 text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
                        Twoje finanse pod
                        <br />
                        <span className="bg-gradient-to-r from-blue-400 to-sky-300 bg-clip-text text-transparent">
                            pełną kontrolą
                        </span>
                    </h1>
                    <p className="mb-8 max-w-xl text-lg leading-relaxed text-white/70">
                        Najdokładniejszy kalkulator podatkowy z integracją AI dla JDG i B2B.
                        Optymalizuj podatki zgodnie z najnowszymi wytycznymi.
                    </p>
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                            <div className="rounded-lg bg-blue-500/20 p-2">
                                <Bolt className="h-6 w-6 text-blue-400" />
                            </div>
                            <span className="font-medium text-white">Analiza AI</span>
                        </div>
                        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                            <div className="rounded-lg bg-emerald-500/20 p-2">
                                <ShieldCheck className="h-6 w-6 text-emerald-400" />
                            </div>
                            <span className="font-medium text-white">Bezpieczeństwo</span>
                        </div>
                    </div>
                </div>

                {/* Widget kalkulatora – pod spodem renderujemy pełny komponent kalkulatora */}
                <div className="relative">
                    <div className="mx-auto max-w-lg rounded-[32px] border border-white/20 bg-white/95 p-6 shadow-2xl shadow-slate-900/40 backdrop-blur-xl">
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                    Przykład
                                </p>
                                <p className="mt-1 text-sm text-slate-500">
                                    Wprowadź własne dane w kalkulatorze poniżej i porównaj formy
                                    opodatkowania.
                                </p>
                            </div>
                            <div className="rounded-3xl bg-blue-500 p-6 text-center text-white shadow-[0_0_40px_-10px_rgba(59,130,246,0.7)]">
                                <span className="text-xs font-medium uppercase tracking-[0.2em] opacity-80">
                                    Zysk na rękę (przykład)
                                </span>
                                <div className="my-2 text-3xl font-extrabold">11 273,24</div>
                                <span className="text-xs font-medium">PLN miesięcznie</span>
                            </div>
                            <p className="text-xs text-slate-500">
                                Poniżej znajdziesz pełny kalkulator z dokładnymi obliczeniami
                                podatków i składek ZUS dla Twojej sytuacji.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Właściwy kalkulator – istniejąca logika, bez zmian */}
        <section className="bg-gray-50 py-10">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <CalculatorComponent token={token} onUpgrade={onUpgrade} />
            </div>
        </section>

        {/* Porównanie form opodatkowania – sekcja marketingowa nad istniejącą funkcją porównania */}
        <section className="bg-white py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="mb-10">
                    <h2 className="mb-3 text-3xl font-extrabold text-slate-900">
                        Wybierz najlepszą formę opodatkowania
                    </h2>
                    <p className="text-lg text-slate-500">
                        Porównaj warianty opodatkowania dostosowane do Twojej branży.
                    </p>
                </div>
                <div className="grid gap-6 md:grid-cols-3">
                    {/* Ryczałt */}
                    <div className="relative flex flex-col rounded-[28px] border-2 border-blue-500/10 bg-white p-6 shadow-sm transition hover:shadow-xl">
                        <span className="absolute right-5 top-5 rounded-lg bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-700">
                            Zalecane
                        </span>
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                            <CheckCircle2 className="h-6 w-6 text-blue-500" />
                        </div>
                        <h3 className="mb-2 text-2xl font-bold text-slate-900">Ryczałt</h3>
                        <p className="mb-6 flex-1 text-sm leading-relaxed text-slate-500">
                            Idealny dla branży IT i usług o niskich kosztach operacyjnych.
                        </p>
                        <div className="mb-6 space-y-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">Podatek</span>
                                <span className="font-semibold text-slate-900">12% / 8.5%</span>
                            </div>
                            <div className="flex items-start justify-between">
                                <span className="text-slate-400">Składka zdrowotna</span>
                                <span className="text-right font-semibold text-slate-900">
                                    Stała (wg
                                    <br />
                                    przychodu)
                                </span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="mt-auto w-full rounded-2xl border-2 border-blue-500 py-3 text-sm font-bold text-blue-600 transition hover:bg-blue-500 hover:text-white"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Wybierz ten model
                        </button>
                    </div>

                    {/* Skala podatkowa */}
                    <div className="flex flex-col rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm transition hover:shadow-xl">
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50">
                            <BarChart3 className="h-6 w-6 text-orange-500" />
                        </div>
                        <h3 className="mb-2 text-2xl font-bold text-slate-900">Skala podatkowa</h3>
                        <p className="mb-6 flex-1 text-sm leading-relaxed text-slate-500">
                            Korzystna przy wysokich kosztach i możliwości rozliczania z
                            małżonkiem.
                        </p>
                        <div className="mb-6 space-y-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">Podatek</span>
                                <span className="font-semibold text-slate-900">12% / 32%</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">Kwota wolna</span>
                                <span className="font-semibold text-slate-900">30 000 PLN</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="mt-auto w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 transition hover:border-blue-500 hover:text-blue-600"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Sprawdź detale
                        </button>
                    </div>

                    {/* Podatek liniowy */}
                    <div className="flex flex-col rounded-[28px] border border-slate-100 bg-white p-6 shadow-sm transition hover:shadow-xl">
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                            <TrendingUp className="h-6 w-6 text-blue-500" />
                        </div>
                        <h3 className="mb-2 text-2xl font-bold text-slate-900">Podatek liniowy</h3>
                        <p className="mb-6 flex-1 text-sm leading-relaxed text-slate-500">
                            Najlepszy dla wysokich dochodów powyżej 120 000 PLN rocznie.
                        </p>
                        <div className="mb-6 space-y-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">Podatek</span>
                                <span className="font-semibold text-slate-900">19% stały</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-400">Zdrowotna</span>
                                <span className="font-semibold text-slate-900">4.9% dochodu</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="mt-auto w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 transition hover:border-blue-500 hover:text-blue-600"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Sprawdź detale
                        </button>
                    </div>
                </div>
            </div>
        </section>

        {/* AI + bezpieczeństwo – sekcja zaufania */}
        <section className="bg-white pb-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-6 md:grid-cols-2">
                    <div className="relative flex items-start gap-5 overflow-hidden rounded-[28px] bg-slate-950 p-8 text-white shadow-xl">
                        <div className="pointer-events-none absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />
                        <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500">
                            <Sparkles className="h-7 w-7 text-white" />
                        </div>
                        <div className="relative">
                            <h4 className="mb-2 text-2xl font-bold">AI powered</h4>
                            <p className="text-sm leading-relaxed text-white/70">
                                Inteligentna optymalizacja Twoich podatków na podstawie tysięcy
                                scenariuszy rynkowych i aktualnych przepisów.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-5 rounded-[28px] border border-slate-100 bg-white p-8 shadow-sm">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50">
                            <ShieldCheck className="h-7 w-7 text-blue-600" />
                        </div>
                        <div>
                            <h4 className="mb-2 text-2xl font-bold text-slate-900">
                                Bezpieczeństwo danych
                            </h4>
                            <p className="text-sm leading-relaxed text-slate-500">
                                Zgodność z najnowszymi wytycznymi Ministerstwa Finansów oraz
                                RODO. Twoje dane są szyfrowane i przetwarzane z zachowaniem
                                najlepszych praktyk bezpieczeństwa.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Dodatkowa sekcja SEO / contentowa – wykorzystujemy istniejący komponent */}
        <section className="bg-slate-50 pb-16">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <Suspense fallback={<div className="p-4 text-center">Ładowanie...</div>}>
                    <SEOContentSection />
                </Suspense>
            </div>
        </section>
    </div>
);

// --- Główny Komponent Aplikacji ---
const App = () => {
    const { showNotification } = useNotification();
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    
    const [activeView, setActiveView] = useState<'calculator' | 'dashboard' | 'comparison' | 'ai-assistant' | 'o-nas' | 'faq' | 'regulamin' | 'polityka-prywatnosci' | 'polityka-cookies' | 'kalkulator-podatkowy-2026' | 'kalkulator-zus-jdg' | 'kalkulator-b2b-2026'>('calculator');
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const handleLogout = () => { localStorage.removeItem('token'); setToken(null); setActiveView('calculator'); };
    
    // Funkcja pomocnicza do obsługi wygasłych tokenów
    const handleTokenExpired = () => {
        localStorage.removeItem('token');
        setToken(null);
        window.dispatchEvent(new CustomEvent('token-expired'));
    };
    
    // Funkcja pomocnicza do fetch z obsługą wygasłych tokenów
    const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
        if (!token) return null;
        
        const response = await fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'x-auth-token': btoa(token)
            }
        });
        
        if (response.status === 401) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.expired) {
                handleTokenExpired();
                return null;
            }
        }
        
        return response;
    };
    
    // Nasłuchuj na event wygasłego tokenu z innych komponentów
    useEffect(() => {
        const handleTokenExpiredEvent = () => {
            handleTokenExpired();
        };
        
        window.addEventListener('token-expired', handleTokenExpiredEvent);
        return () => window.removeEventListener('token-expired', handleTokenExpiredEvent);
    }, []);
    
    // Funkcja do obsługi upgrade subskrypcji
    const handleUpgrade = async (plan: 'pro' | 'expert') => {
        if (!token) {
            setIsAuthModalOpen(true);
            return;
        }
        
        try {
            const response = await fetch('/api/subscription/create-checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': btoa(token)
                },
                body: JSON.stringify({ plan })
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Błąd tworzenia sesji płatności');
            }
            
            const { url } = await response.json();
            // Przekieruj do Stripe Checkout
            window.location.href = url;
        } catch (error: any) {
            showNotification({
                type: 'error',
                title: 'Błąd',
                message: error.message || 'Wystąpił nieoczekiwany błąd'
            });
            console.error('Błąd upgrade:', error);
        }
    };
    
    // Funkcja do aktualizacji meta tagów SEO
    const updateMetaTags = (title: string, description: string, keywords?: string, canonical?: string) => {
        // Aktualizuj title
        document.title = title;
        
        // Aktualizuj meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.setAttribute('name', 'description');
            document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', description);
        
        // Aktualizuj meta keywords jeśli podano
        if (keywords) {
            let metaKeywords = document.querySelector('meta[name="keywords"]');
            if (!metaKeywords) {
                metaKeywords = document.createElement('meta');
                metaKeywords.setAttribute('name', 'keywords');
                document.head.appendChild(metaKeywords);
            }
            metaKeywords.setAttribute('content', keywords);
        }
        
        // Aktualizuj Open Graph title
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
        }
        ogTitle.setAttribute('content', title);
        
        // Aktualizuj Open Graph description
        let ogDescription = document.querySelector('meta[property="og:description"]');
        if (!ogDescription) {
            ogDescription = document.createElement('meta');
            ogDescription.setAttribute('property', 'og:description');
            document.head.appendChild(ogDescription);
        }
        ogDescription.setAttribute('content', description);
        
        // Aktualizuj canonical URL jeśli podano
        if (canonical) {
            let canonicalLink = document.querySelector('link[rel="canonical"]');
            if (!canonicalLink) {
                canonicalLink = document.createElement('link');
                canonicalLink.setAttribute('rel', 'canonical');
                document.head.appendChild(canonicalLink);
            }
            canonicalLink.setAttribute('href', canonical);
        }
    };

    // Obsługa nawigacji do stron prawnych i hash (#dashboard) + aktualizacja SEO
    useEffect(() => {
        const handleNavigation = () => {
            const path = window.location.pathname;
            const hash = window.location.hash;
            
            // Track page view on navigation
            trackPageView(activeView, document.title);
            
            if (path === '/regulamin') {
                setActiveView('regulamin');
                updateMetaTags(
                    'Regulamin - Kalkulator JDG 2026 | KalkulatorJDG.pl',
                    'Regulamin korzystania z kalkulatora JDG 2026. Przeczytaj zasady korzystania z kalkulatora podatkowego dla działalności gospodarczej.',
                    'regulamin kalkulator jdg, zasady korzystania, polityka kalkulator podatkowy'
                );
            }
            else if (path === '/polityka-prywatnosci') {
                setActiveView('polityka-prywatnosci');
                updateMetaTags(
                    'Polityka Prywatności - Kalkulator JDG 2026 | KalkulatorJDG.pl',
                    'Polityka prywatności kalkulatora JDG 2026. Dowiedz się jak chronimy Twoje dane osobowe w kalkulatorze podatkowym.',
                    'polityka prywatności kalkulator jdg, ochrona danych, RODO'
                );
            }
            else if (path === '/polityka-cookies') {
                setActiveView('polityka-cookies');
                updateMetaTags(
                    'Polityka Cookies - Kalkulator JDG 2026 | KalkulatorJDG.pl',
                    'Polityka cookies kalkulatora JDG 2026. Informacje o wykorzystaniu plików cookies w kalkulatorze podatkowym.',
                    'polityka cookies kalkulator jdg, ciasteczka, cookies'
                );
            }
            else if (path === '/kalkulator-podatkowy-2026') {
                setActiveView('kalkulator-podatkowy-2026');
                updateMetaTags(
                    'Kalkulator Podatkowy 2026 - Wszystkie Formy Opodatkowania JDG | KalkulatorJDG.pl',
                    'Kalkulator podatkowy 2026 dla działalności gospodarczej. Oblicz podatki, porównaj ryczałt, skalę i podatek liniowy. Najlepszy kalkulator podatkowy 2026 z AI.',
                    'kalkulator podatkowy 2026, kalkulator podatków 2026, oblicz podatki jdg, porównanie form opodatkowania',
                    'https://kalkulatorjdg.pl/kalkulator-podatkowy-2026'
                );
            }
            else if (path === '/kalkulator-zus-jdg') {
                setActiveView('kalkulator-zus-jdg');
                updateMetaTags(
                    'Kalkulator ZUS JDG 2026 - Oblicz Składki ZUS Online | KalkulatorJDG.pl',
                    'Kalkulator ZUS JDG 2026 - oblicz wszystkie składki ZUS dla działalności gospodarczej. Duży ZUS, Mały ZUS, Preferencyjny ZUS. Aktualne stawki ZUS 2026.',
                    'kalkulator zus jdg, kalkulator zus 2026, składki zus działalność gospodarcza, oblicz zus, duży zus, mały zus',
                    'https://kalkulatorjdg.pl/kalkulator-zus-jdg'
                );
            }
            else if (path === '/kalkulator-b2b-2026') {
                setActiveView('kalkulator-b2b-2026');
                updateMetaTags(
                    'Kalkulator B2B 2026 - Oblicz Podatki dla Kontraktów B2B | KalkulatorJDG.pl',
                    'Kalkulator B2B 2026 - oblicz podatki dla działalności z kontraktami B2B. Kalkulator podatkowy B2B 2026 z AI. Oblicz składki ZUS dla B2B.',
                    'kalkulator b2b 2026, b2b kalkulator 2026, podatki b2b, kalkulator podatkowy b2b',
                    'https://kalkulatorjdg.pl/kalkulator-b2b-2026'
                );
            }
            else if (hash === '#dashboard' && token) {
                setActiveView('dashboard');
                updateMetaTags(
                    'Panel Użytkownika - Kalkulator JDG 2026 | KalkulatorJDG.pl',
                    'Panel użytkownika kalkulatora JDG 2026. Zarządzaj zapisanymi obliczeniami, historią czatów AI i subskrypcją.',
                    'panel użytkownika kalkulator jdg, konto użytkownika, zapisane obliczenia'
                );
                // Sprawdź czy pokazać subskrypcję
                if (localStorage.getItem('showSubscription') === 'true') {
                    localStorage.removeItem('showSubscription');
                    // Wywołaj event po małym opóźnieniu aby Dashboard był już zamontowany
                    setTimeout(() => {
                        const event = new Event('show-subscription');
                        window.dispatchEvent(event);
                    }, 200);
                }
            }
            else {
                setActiveView('calculator');
                updateMetaTags(
                    'Kalkulator JDG 2026 - Najlepszy Kalkulator Podatkowy z AI | KalkulatorJDG.pl',
                    'Kalkulator JDG 2026 - profesjonalny kalkulator podatkowy dla działalności gospodarczej. Oblicz podatki, składki ZUS, porównaj formy opodatkowania. Kalkulator B2B 2026, kalkulator podatkowy 2026, kalkulator zus jdg. Darmowy i precyzyjny kalkulator działalności gospodarczej z AI.',
                    'kalkulator jdg 2026, kalkulator podatkowy 2026, kalkulator b2b 2026, kalkulator zus jdg, kalkulator jdg, jdg kalkulator, kalkulator działalności gospodarczej, porównanie form opodatkowania, podatki 2026, zus 2026, ryczałt ewidencjonowany, skala podatkowa, podatek liniowy',
                    'https://kalkulatorjdg.pl'
                );
            }
        };
        
        window.addEventListener('popstate', handleNavigation);
        window.addEventListener('hashchange', handleNavigation);
        handleNavigation(); // Sprawdź początkowy stan
        
        // Initialize analytics tracking
        const scrollCleanup = initScrollTracking();
        const timeCleanup = initTimeTracking(activeView);
        
        return () => {
            window.removeEventListener('popstate', handleNavigation);
            window.removeEventListener('hashchange', handleNavigation);
            if (scrollCleanup) scrollCleanup();
            if (timeCleanup) timeCleanup();
        };
    }, [token]);
    
    const navigateToLegal = (page: 'regulamin' | 'polityka-prywatnosci' | 'polityka-cookies') => {
        setActiveView(page);
        window.history.pushState({}, '', `/${page}`);
    };
    
    const navigateBack = () => {
        setActiveView('calculator');
        window.history.pushState({}, '', '/');
    };
    
    return (
        <div className="bg-gray-50 min-h-screen flex flex-col">
            {isAuthModalOpen && <AuthModal setToken={setToken} onClose={() => setIsAuthModalOpen(false)} />}
            <nav className="bg-slate-950/95 border-b border-white/10 backdrop-blur sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        {/* Logo */}
                        <div className="flex items-center gap-2">
                            <img
                                src="/logo.png"
                                alt="Kalkulator JDG"
                                className="h-8 w-auto sm:h-10"
                                onError={(e) => {
                                    const img = e.currentTarget as HTMLImageElement;
                                    img.style.display = 'none';
                                    const fallback = img.nextElementSibling as HTMLElement | null;
                                    if (fallback) fallback.style.display = 'inline';
                                }}
                            />
                            <span
                                className="text-base font-extrabold tracking-tight text-white sm:text-lg md:text-2xl"
                                style={{ display: 'none' }}
                            >
                                Kalkulator
                                <span className="text-blue-400">JDG</span>
                            </span>
                        </div>
                        {/* Desktop Menu */}
                        <div className="hidden items-center space-x-4 md:flex">
                            {token ? (
                                <>
                                    <button
                                        onClick={() => { setActiveView('calculator'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        className={`flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${activeView === 'calculator' ? 'bg-white text-slate-900' : 'text-slate-200 hover:text-white'}`}
                                    >
                                        Kalkulator
                                    </button>
                                    <button
                                        onClick={() => setActiveView('comparison')}
                                        className={`flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${activeView === 'comparison' ? 'bg-white text-slate-900' : 'text-slate-200 hover:text-white'}`}
                                    >
                                        Porównanie
                                    </button>
                                    <button
                                        onClick={() => setActiveView('ai-assistant')}
                                        className={`flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${activeView === 'ai-assistant' ? 'bg-white text-slate-900' : 'text-slate-200 hover:text-white'}`}
                                    >
                                        AI optymalizacja
                                    </button>
                                    <button
                                        onClick={() => setActiveView('dashboard')}
                                        className={`flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${activeView === 'dashboard' ? 'bg-white text-slate-900' : 'text-slate-200 hover:text-white'}`}
                                    >
                                        Panel
                                    </button>
                                    <a
                                        href="https://blog.kalkulatorjdg.pl"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-slate-200 hover:text-white"
                                    >
                                        Blog
                                    </a>
                                    <button
                                        onClick={handleLogout}
                                        className="text-xs font-medium text-slate-300 hover:text-white"
                                    >
                                        <LogOut size={16} className="mr-1 inline" />
                                        Wyloguj
                                    </button>
                                </>
                            ) : (
                                <>
                                <button
                                    onClick={() => { setActiveView('o-nas'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    className="rounded-full px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:text-white"
                                >
                                    O nas
                                </button>
                                <button
                                    onClick={() => { setActiveView('faq'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                    className="rounded-full px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:text-white"
                                >
                                    FAQ
                                </button>
                                <a
                                    href="https://blog.kalkulatorjdg.pl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-medium text-slate-200 hover:text-white"
                                >
                                    Blog
                                </a>
                                <button
                                    onClick={() => setIsAuthModalOpen(true)}
                                    className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
                                >
                                    <LogIn size={16} className="mr-1 inline" />
                                    Zaloguj się
                                </button>
                                </>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="rounded-md p-2 text-slate-200 hover:text-white"
                                aria-label={isMobileMenuOpen ? "Zamknij menu nawigacji" : "Otwórz menu nawigacji"}
                                aria-expanded={isMobileMenuOpen}
                                aria-controls="mobile-navigation"
                            >
                                <Menu size={24} />
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {isMobileMenuOpen && (
                        <div className="md:hidden" id="mobile-navigation">
                            <div className="space-y-1 border-t bg-slate-900/95 px-2 pt-2 pb-3 sm:px-3">
                                {token ? (
                                    <>
                                        <button 
                                            onClick={() => { setActiveView('calculator'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <Calculator size={20} className="mr-3"/> Kalkulator
                                        </button>
                                        <button 
                                            onClick={() => { setActiveView('comparison'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <TrendingUp size={20} className="mr-3"/> Porównanie
                                        </button>
                                        <button 
                                            onClick={() => { setActiveView('ai-assistant'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <Bot size={20} className="mr-3"/> AI Asystent
                                        </button>
                                        <button 
                                            onClick={() => { setActiveView('dashboard'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <User size={20} className="mr-3"/> Panel
                                        </button>
                                        <button 
                                            onClick={() => { setActiveView('o-nas'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <FileText size={20} className="mr-3"/> O nas
                                        </button>
                                        <button 
                                            onClick={() => { setActiveView('faq'); setIsMobileMenuOpen(false); }} 
                                            className="text-slate-600 hover:text-blue-600 block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center"
                                        >
                                            <AlertTriangle size={20} className="mr-3"/> FAQ
                                        </button>
                                        <a 
                                            href="https://blog.kalkulatorjdg.pl" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="flex w-full items-center rounded-md bg-slate-800 px-3 py-2 text-left text-base font-medium text-slate-100 hover:bg-slate-700"
                                        >
                                            <FileText size={20} className="mr-3"/> Blog
                                        </a>
                                        <button 
                                            onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                        >
                                            <LogOut size={20} className="mr-3"/> Wyloguj
                                        </button>
                                    </>
                                ) : (
                                    <>
                                    <button 
                                        onClick={() => { setIsAuthModalOpen(true); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                    >
                                        <LogIn size={20} className="mr-3"/> Zaloguj się
                                    </button>
                                    <button 
                                        onClick={() => { setActiveView('o-nas'); setIsMobileMenuOpen(false); }} 
                                            className="flex w-full items-center rounded-md px-3 py-2 text-left text-base font-medium text-slate-200 hover:bg-slate-800"
                                    >
                                        <FileText size={20} className="mr-3"/> O nas
                                    </button>
                                    <button 
                                        onClick={() => { setActiveView('faq'); setIsMobileMenuOpen(false); }} 
                                        className="text-slate-600 hover:text-blue-600 block w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center"
                                    >
                                        <AlertTriangle size={20} className="mr-3"/> FAQ
                                    </button>
                                        <a 
                                            href="https://blog.kalkulatorjdg.pl" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="flex w-full items-center rounded-md bg-slate-800 px-3 py-2 text-left text-base font-medium text-slate-100 hover:bg-slate-700"
                                        >
                                            <FileText size={20} className="mr-3"/> Blog
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </nav>
            <main className="flex-grow bg-slate-50">
                {activeView === 'regulamin' ? (
                        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <button 
                            onClick={navigateBack}
                            className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
                        >
                            <ArrowLeft size={20} className="mr-2" />
                            Powrót do kalkulatora
                        </button>
                        <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                            <LegalPage type="regulamin" />
                        </Suspense>
                            </div>
                ) : activeView === 'polityka-prywatnosci' ? (
                        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                            <button
                            onClick={navigateBack}
                            className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
                            >
                            <ArrowLeft size={20} className="mr-2" />
                            Powrót do kalkulatora
                            </button>
                        <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                            <LegalPage type="polityka-prywatnosci" />
                        </Suspense>
                    </div>
                ) : activeView === 'polityka-cookies' ? (
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                            <button
                            onClick={navigateBack}
                            className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
                            >
                            <ArrowLeft size={20} className="mr-2" />
                            Powrót do kalkulatora
                            </button>
                        <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                            <LegalPage type="polityka-cookies" />
                        </Suspense>
                        </div>
                ) : activeView === 'o-nas' ? (
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                            <SEOContentSection />
                        </Suspense>
                    </div>
                ) : activeView === 'faq' ? (
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <Suspense fallback={<div className="p-8 text-center">Ładowanie...</div>}>
                            <FAQSection />
                        </Suspense>
                    </div>
                ) : 
                 activeView === 'comparison' ? <TaxFormComparison token={token} onUpgrade={handleUpgrade} /> :
                 activeView === 'ai-assistant' ? <AIAssistant token={token} /> :
                 activeView === 'calculator' || !token ? (
                     <RedesignedCalculatorPage token={token} onUpgrade={handleUpgrade} />
                 ) :
                 <Dashboard token={token} onUpgrade={handleUpgrade} onMetaUpdate={updateMetaTags} />}
            </main>
            <Footer onNavigateToLegal={navigateToLegal} />
            <AccountantChatbot token={token} onTokenExpired={handleTokenExpired} />
        </div>
    );
};

const ResultRow = ({ label, value, isBold = false }: { label: string, value: number, isBold?: boolean }) => (
    <div className={`flex justify-between items-center p-3 rounded-xl ${isBold ? 'bg-slate-100' : 'bg-slate-50/80'}`}>
        <span className={`text-slate-700 ${isBold ? 'font-semibold' : ''}`}>{label}</span>
        <span className={`font-mono ${isBold ? 'font-bold text-lg' : ''} ${value >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
            {value.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
        </span>
    </div>
);

export default App;
